import { overtimePay } from "../utils/monthlySalary";
import { registerStyles } from "../utils/registerStyles";
import useVisibleProjects from "../hooks/useVisibleProjects";
import { calculateTotalShiftMinutes, calculateTotalBreakMinutes, calculateShiftHours, calculateOvertimeForShift } from "../utils/timeReportHours";
import { resolvePayrollTime, workTimeHours } from "../utils/payrollTime";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaChevronLeft, FaChevronRight, FaPlus, FaEdit, FaTrash, FaSave, FaMagic, FaLock, FaInfoCircle } from "react-icons/fa";
import { collection, onSnapshot, query, where, doc, setDoc, deleteDoc, addDoc } from "firebase/firestore";
import { db } from "../firebase";
import { calculateAbsencePay } from "../utils/absencePay";

import { createPortal } from "react-dom";

function ReportTimeSource({ row }) {
  const punched = row.employeeData?.payrollTime?.source === "punched";
  return (
    <div className="report-time-source">
      <span className={`report-time-source-badge${punched ? " report-time-source-badge--punched" : ""}`}>
        {punched ? "Enligt tidsstämplingen" : "Schemalagd"}
      </span>
      {punched && (
        <details className="report-time-source-details">
          <summary aria-label="Visa tiden före och efter ändringen" title="Visa tidsändring">
            <FaInfoCircle aria-hidden="true" />
          </summary>
          <div className="report-time-source-history">
            <span>Föregående tid (schema): <strong>{row.scheduledStartTime || "–"} – {row.scheduledEndTime || "–"}</strong></span>
            <span>Ny tid: <strong>{row.shiftStartTime} – {row.shiftEndTime}</strong></span>
          </div>
        </details>
      )}
    </div>
  );
}

function ReportObLockInfo({ small = false }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(260, window.innerWidth - 16);
      setPosition({
        left: Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8)),
        top: rect.bottom + 120 < window.innerHeight ? rect.bottom + 6 : Math.max(8, rect.top - 126),
        width,
      });
    };
    const closeOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") { setOpen(false); buttonRef.current?.focus(); }
    };
    document.addEventListener("keydown", closeOnEscape);
    updatePosition();
    document.addEventListener("mousedown", closeOutside);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("mousedown", closeOutside);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  return (
    <span ref={wrapperRef} className="report-payroll-lock" onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        ref={buttonRef}
        className="report-payroll-lock-button"
        aria-label="Varför är OB låst?"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <FaLock size={small ? 10 : 12} aria-hidden="true" />
      </button>
      {open && position && createPortal(
        <span className="report-payroll-lock-bubble" role="status" style={position}>
          OB är låst eftersom personens löneunderlag är godkänt i Löneexport.
          Avmarkera Godkänd för personen där för att kunna ändra OB.
        </span>,
        document.body
      )}
    </span>
  );
}

const VACATION_PAY_PERCENT = 12;

function pad(n) {
  return String(n).padStart(2, "0");
}

function safeParseDate(dateValue) {
  if (!dateValue) return null;

  try {
    if (dateValue instanceof Date) {
      return Number.isNaN(dateValue.getTime()) ? null : dateValue;
    }

    if (
      typeof dateValue === "object" &&
      typeof dateValue.toDate === "function"
    ) {
      const parsed = dateValue.toDate();
      return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : null;
    }

    if (typeof dateValue === "string" || typeof dateValue === "number") {
      const parsed = new Date(dateValue);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
  } catch (error) {
    console.error("Fel vid datumtolkning:", error);
    return null;
  }
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function formatDateKey(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function timeToMinutes(timeString) {
  if (!timeString || typeof timeString !== "string") return 0;

  const [h, m] = timeString.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return 0;
  return h * 60 + m;
}

function formatTimeFromMinutes(minutes) {
  const normalizedMinutes = Math.max(0, Math.min(1440, Number(minutes || 0)));
  const hours = Math.floor(normalizedMinutes / 60);
  const mins = normalizedMinutes % 60;
  return `${pad(hours)}:${pad(mins)}`;
}

function normalizeDayName(day) {
  const map = {
    monday: "måndag",
    tuesday: "tisdag",
    wednesday: "onsdag",
    thursday: "torsdag",
    friday: "fredag",
    saturday: "lördag",
    sunday: "söndag",
    måndag: "måndag",
    tisdag: "tisdag",
    onsdag: "onsdag",
    torsdag: "torsdag",
    fredag: "fredag",
    lördag: "lördag",
    söndag: "söndag",
    Måndag: "måndag",
    Tisdag: "tisdag",
    Onsdag: "onsdag",
    Torsdag: "torsdag",
    Fredag: "fredag",
    Lördag: "lördag",
    Söndag: "söndag",
  };

  return map[String(day || "").trim()] || String(day || "").toLowerCase().trim();
}

function getSwedishWeekday(date) {
  const names = [
    "söndag",
    "måndag",
    "tisdag",
    "onsdag",
    "torsdag",
    "fredag",
    "lördag",
  ];
  return names[date.getDay()];
}

function formatHours(hours) {
  return `${Number(hours || 0).toLocaleString("sv-SE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}h`;
}

function formatCurrency(value) {
  return `${Number(value || 0).toLocaleString("sv-SE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}kr`;
}

function formatNegativeCurrency(value) {
  if (Math.abs(Number(value || 0)) < 0.005) return formatCurrency(0);
  return `- ${Number(value || 0).toLocaleString("sv-SE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}kr`;
}

function formatDate(date) {
  if (!date) return "-";
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

function getMonthLabel(date) {
  return date
    .toLocaleDateString("sv-SE", {
      month: "long",
      year: "numeric",
    })
    .replace(/^./, (str) => str.toUpperCase());
}

function calculateAbsenceForShift(shift, employeeData = null) {
  ({ shift, employeeData } = resolvePayrollTime(shift, employeeData));
  if (!shift?.startTime || !shift?.endTime) return { hours: 0, value: 0 };

  try {
    let absenceMinutes = 0;
    let absenceValue = 0;

    if (employeeData?.absences && Array.isArray(employeeData.absences)) {
      employeeData.absences.forEach((absenceItem) => {
        let itemMinutes = 0;

        if (absenceItem.appliesToWholeShift) {
          const totalMinutes = calculateTotalShiftMinutes(shift);
          const breakMinutes = calculateTotalBreakMinutes(employeeData);
          itemMinutes = Math.max(0, totalMinutes - breakMinutes);
        } else if (absenceItem.startTime && absenceItem.endTime) {
          const start = timeToMinutes(absenceItem.startTime);
          const end = timeToMinutes(absenceItem.endTime);

          let minutes = end - start;
          if (minutes < 0) {
            minutes += 24 * 60;
          }
          itemMinutes = minutes;
        } else if (Number(absenceItem.hours || 0) > 0) {
          itemMinutes = Number(absenceItem.hours || 0) * 60;
        }

        absenceMinutes += itemMinutes;

        const salaryPercent = Number(absenceItem.absencePercent ?? absenceItem.salaryPercent ?? 0);
        const deductionPercent = (100 - salaryPercent) / 100;
        absenceValue += (itemMinutes / 60) * deductionPercent;
      });
    }

    return {
      hours: absenceMinutes / 60,
      value: absenceValue,
    };
  } catch (error) {
    console.error("Fel vid beräkning av frånvaro:", error);
    return { hours: 0, value: 0 };
  }
}

function getShiftSegments(shift) {
  const shiftDate = safeParseDate(shift?.date);
  if (!shiftDate || !shift?.startTime || !shift?.endTime) return [];

  const startMinutes = timeToMinutes(String(shift.startTime));
  const endMinutes = timeToMinutes(String(shift.endTime));

  if (startMinutes === endMinutes) return [];

  if (endMinutes > startMinutes) {
    return [
      {
        date: new Date(shiftDate),
        dateKey: formatDateKey(shiftDate),
        startMinute: startMinutes,
        endMinute: endMinutes,
      },
    ];
  }

  const nextDate = addDays(shiftDate, 1);

  return [
    {
      date: new Date(shiftDate),
      dateKey: formatDateKey(shiftDate),
      startMinute: startMinutes,
      endMinute: 1440,
    },
    {
      date: new Date(nextDate),
      dateKey: formatDateKey(nextDate),
      startMinute: 0,
      endMinute: endMinutes,
    },
  ];
}

function getCustomObSegmentsForShift(shift, customObEntries = []) {
  const shiftDate = safeParseDate(shift?.date);
  if (!shiftDate) return [];

  const shiftId = normalizeId(shift?.originalId || shift?.id);
  const shiftDateKey = formatDateKey(shiftDate);

  return (customObEntries || [])
    .filter((customOb) => {
      const customShiftId = normalizeId(customOb?.shiftId);
      if (customShiftId && shiftId) return customShiftId === shiftId;

      // Bakåtkompatibilitet för äldre poster som saknar shiftId.
      return getDateKey(customOb?.date) === shiftDateKey;
    })
    .flatMap((customOb) => {
      const customDate = safeParseDate(customOb?.date);
      if (!customDate || !customOb?.startTime || !customOb?.endTime) return [];

      const startMinute = timeToMinutes(customOb.startTime);
      const endMinute = timeToMinutes(customOb.endTime);

      if (startMinute === endMinute) return [];

      if (endMinute > startMinute) {
        return [{
          date: customDate,
          dateKey: formatDateKey(customDate),
          startMinute,
          endMinute,
          isCustomExtension: true,
        }];
      }

      const nextDate = addDays(customDate, 1);
      return [
        {
          date: customDate,
          dateKey: formatDateKey(customDate),
          startMinute,
          endMinute: 1440,
          isCustomExtension: true,
        },
        {
          date: nextDate,
          dateKey: formatDateKey(nextDate),
          startMinute: 0,
          endMinute,
          isCustomExtension: true,
        },
      ];
    });
}

function mergeTimelineSegments(segments = []) {
  const byDate = new Map();

  segments.forEach((segment) => {
    if (!segment || segment.endMinute <= segment.startMinute) return;
    const key = segment.dateKey || formatDateKey(segment.date);
    if (!byDate.has(key)) byDate.set(key, []);
    byDate.get(key).push(segment);
  });

  const merged = [];
  Array.from(byDate.entries()).forEach(([dateKey, dateSegments]) => {
    dateSegments
      .sort((a, b) => a.startMinute - b.startMinute)
      .forEach((segment) => {
        const last = merged[merged.length - 1];
        if (last && last.dateKey === dateKey && segment.startMinute <= last.endMinute) {
          last.endMinute = Math.max(last.endMinute, segment.endMinute);
          return;
        }
        merged.push({ ...segment, dateKey });
      });
  });

  return merged.sort((a, b) => {
    const dateDiff = a.date.getTime() - b.date.getTime();
    return dateDiff || a.startMinute - b.startMinute;
  });
}

function getObTimelineSegments(shift, customObEntries = []) {
  return mergeTimelineSegments([
    ...getShiftSegments(shift),
    ...getCustomObSegmentsForShift(shift, customObEntries),
  ]);
}

function isMinuteInsideShift(shiftSegments, dateKey, minute) {
  return (shiftSegments || []).some(
    (segment) =>
      segment.dateKey === dateKey &&
      minute >= segment.startMinute &&
      minute < segment.endMinute
  );
}

// UPPDATERAD: Använd specifika tider för helgdagar
function buildSummedObRateMapForDate(date, obRules, holidays, customObEntries) {
  const rateMap = Array(1440).fill(0);

  const currentDateKey = formatDateKey(date);
  const previousDate = addDays(date, -1);
  const previousDateKey = formatDateKey(previousDate);

  const currentWeekday = getSwedishWeekday(date);
  const previousWeekday = getSwedishWeekday(previousDate);

  (obRules || []).forEach((rule) => {
    if (rule?.useSpecificDates) {
      const specificDates = Array.isArray(rule.specificDates) ? rule.specificDates : [];

      specificDates.forEach((item) => {
        const itemDate = item?.date;
        const amount = Number(item?.amount ?? rule?.amount ?? 0);
        const start = timeToMinutes(item?.startTime || rule?.startTime || "00:00");
        const end = timeToMinutes(item?.endTime || rule?.endTime || "23:59");

        if (amount <= 0) return;

        if (start <= end) {
          if (itemDate === currentDateKey) {
            for (let minute = start; minute < end; minute++) {
              rateMap[minute] += amount;
            }
          }
        } else {
          if (itemDate === currentDateKey) {
            for (let minute = start; minute < 1440; minute++) {
              rateMap[minute] += amount;
            }
          }
          if (itemDate === previousDateKey) {
            for (let minute = 0; minute < end; minute++) {
              rateMap[minute] += amount;
            }
          }
        }
      });

      return;
    }

    const days = Array.isArray(rule?.days)
      ? rule.days.map(normalizeDayName)
      : [];

    const amount = Number(rule?.amount || 0);
    const start = timeToMinutes(rule?.startTime || "00:00");
    const end = timeToMinutes(rule?.endTime || "23:59");

    if (amount <= 0) return;

    if (start <= end) {
      if (days.length === 0 || days.includes(currentWeekday)) {
        for (let minute = start; minute < end; minute++) {
          rateMap[minute] += amount;
        }
      }
    } else {
      if (days.length === 0 || days.includes(currentWeekday)) {
        for (let minute = start; minute < 1440; minute++) {
          rateMap[minute] += amount;
        }
      }
      if (days.length === 0 || days.includes(previousWeekday)) {
        for (let minute = 0; minute < end; minute++) {
          rateMap[minute] += amount;
        }
      }
    }
  });

  // UPPDATERAD: Helgdagar använder nu specifika tider istället för hela dagen
  (holidays || []).forEach((holiday) => {
    const holidayDateKey = holiday?.date;
    const amount = Number(holiday?.amount || 0);

    if (amount <= 0) return;

    // Hämta start- och sluttid från helgdagen
    const start = timeToMinutes(holiday?.startTime || "00:00");
    const end = timeToMinutes(holiday?.endTime || "23:59");

    const isCurrentDate = holidayDateKey === currentDateKey;
    const isPreviousDate = holidayDateKey === previousDateKey;

    if (start <= end) {
      // Normalt tidsintervall (start < end)
      if (isCurrentDate) {
        for (let minute = start; minute < end; minute++) {
          rateMap[minute] += amount;
        }
      }
    } else {
      // Övernattande tidsintervall (start > end, t.ex. 22:00-06:00)
      if (isCurrentDate) {
        for (let minute = start; minute < 1440; minute++) {
          rateMap[minute] += amount;
        }
      }
      if (isPreviousDate) {
        for (let minute = 0; minute < end; minute++) {
          rateMap[minute] += amount;
        }
      }
    }
  });

  (customObEntries || []).forEach((customOb) => {
    const customDateKey = customOb?.date;
    const amount = Number(customOb?.amount || 0);
    const start = timeToMinutes(customOb?.startTime || "00:00");
    const end = timeToMinutes(customOb?.endTime || "23:59");

    if (amount <= 0) return;

    if (start <= end) {
      if (customDateKey === currentDateKey) {
        for (let minute = start; minute < end; minute++) {
          rateMap[minute] += amount;
        }
      }
    } else {
      if (customDateKey === currentDateKey) {
        for (let minute = start; minute < 1440; minute++) {
          rateMap[minute] += amount;
        }
      }
      if (customDateKey === previousDateKey) {
        for (let minute = 0; minute < end; minute++) {
          rateMap[minute] += amount;
        }
      }
    }
  });

  return rateMap;
}

function buildAbsenceIntervals(segments, employeeData) {
  const absenceIntervals = [];

  if (employeeData?.absences && Array.isArray(employeeData.absences)) {
    employeeData.absences.forEach((absenceItem) => {
      if (absenceItem.appliesToWholeShift) {
        segments.forEach((segment) => {
          absenceIntervals.push({
            start: segment.startMinute,
            end: segment.endMinute,
          });
        });
      } else if (absenceItem.startTime && absenceItem.endTime) {
        const start = timeToMinutes(absenceItem.startTime);
        const end = timeToMinutes(absenceItem.endTime);

        if (end < start) {
          segments.forEach((segment) => {
            if (segment.startMinute <= start && segment.endMinute >= 1440) {
              absenceIntervals.push({ start, end: 1440 });
            }
            if (segment.startMinute <= end && segment.endMinute >= 0) {
              absenceIntervals.push({ start: 0, end });
            }
          });
        } else {
          segments.forEach((segment) => {
            const overlapStart = Math.max(segment.startMinute, start);
            const overlapEnd = Math.min(segment.endMinute, end);
            if (overlapEnd > overlapStart) {
              absenceIntervals.push({
                start: overlapStart,
                end: overlapEnd,
              });
            }
          });
        }
      }
    });
  }

  return absenceIntervals;
}

function applyObCalculationMode(activeSources, obCalculationMode = "combined") {
  const validSources = (activeSources || []).filter(
    (source) => Number(source?.amount || 0) > 0
  );

  if (obCalculationMode !== "holidayPriority") {
    return validSources;
  }

  const holidaySources = validSources.filter(
    (source) => source?.type === "holiday"
  );

  return holidaySources.length > 0 ? holidaySources : validSources;
}

function getAutomaticCarrySource(activeSources = []) {
  const eligible = (activeSources || []).filter(
    (source) =>
      Number(source?.amount || 0) > 0 &&
      source?.type !== "custom" &&
      source?.type !== "automatic"
  );

  if (eligible.length === 0) return null;

  return [...eligible].sort((a, b) => {
    const holidayDiff = Number(b?.type === "holiday") - Number(a?.type === "holiday");
    if (holidayDiff !== 0) return holidayDiff;
    return Number(b?.amount || 0) - Number(a?.amount || 0);
  })[0];
}

function makeAutomaticObSource(source, manualAmount = null) {
  if (!source) return null;

  const parsedManualAmount = Number(manualAmount);
  const hasManualAmount = Number.isFinite(parsedManualAmount) && parsedManualAmount > 0;

  return {
    ...source,
    amount: hasManualAmount ? parsedManualAmount : source.amount,
    name: source.name || "OB",
    type: "automatic",
    sourceId: `automatic__${source.sourceId || source.name || "ob"}`,
    originalSourceType: source.type,
  };
}

function shiftCrossesMidnight(shift) {
  if (!shift?.startTime || !shift?.endTime) return false;
  return timeToMinutes(String(shift.endTime)) < timeToMinutes(String(shift.startTime));
}

function getObSourceBadge(sourceType) {
  const badges = {
    automatic: {
      label: "Automatisk",
      background: "#dcfce7",
      color: "#166534",
      border: "#86efac",
    },
    custom: {
      label: "Manuell",
      background: "#dbeafe",
      color: "#1d4ed8",
      border: "#93c5fd",
    },
    holiday: {
      label: "Helg-OB",
      background: "#f3e8ff",
      color: "#7e22ce",
      border: "#d8b4fe",
    },
    obRule: {
      label: "OB-regel",
      background: "#f1f5f9",
      color: "#475569",
      border: "#cbd5e1",
    },
  };

  return badges[sourceType] || badges.obRule;
}


function calculateObForShift(
  shift,
  obRules,
  holidays,
  obEnabled = true,
  employeeData = null,
  customObEntries = [],
  obCalculationMode = "combined",
  automaticObContinuation = false,
  automaticObManualAmount = null
) {
  ({ shift, employeeData } = resolvePayrollTime(shift, employeeData));
  if (!obEnabled) {
    return { hours: 0, value: 0 };
  }

  const shiftSegments = getShiftSegments(shift);
  const segments = getObTimelineSegments(shift, customObEntries);
  if (segments.length === 0) {
    return { hours: 0, value: 0 };
  }

  const absenceIntervals = buildAbsenceIntervals(shiftSegments, employeeData);

  const isMinuteAbsent = (minute) => {
    return absenceIntervals.some(
      (interval) => minute >= interval.start && minute < interval.end
    );
  };

  let obMinutes = 0;
  let obValue = 0;
  let automaticCarrySource = null;
  const canContinueAutomatically =
    automaticObContinuation && shiftCrossesMidnight(shift);

  segments.forEach((segment) => {
    for (let minute = segment.startMinute; minute < segment.endMinute; minute++) {
      if (isMinuteAbsent(minute)) continue;

      let activeSources = applyObCalculationMode(
        getActiveObSourcesAtMinute(
          minute,
          segment.date,
          obRules,
          holidays,
          customObEntries
        ),
        obCalculationMode
      );

      const minuteIsInsideShift = isMinuteInsideShift(
        shiftSegments,
        segment.dateKey,
        minute
      );

      // Utanför det ordinarie passet räknas endast den manuella
      // OB-förlängningen, inte vanliga OB-regler eller automatisk fortsättning.
      if (!minuteIsInsideShift) {
        activeSources = activeSources.filter((source) => source.type === "custom");
      } else if (canContinueAutomatically) {
        const latestSource = getAutomaticCarrySource(activeSources);

        if (latestSource) {
          automaticCarrySource = latestSource;
        } else if (activeSources.length === 0 && automaticCarrySource) {
          activeSources = [makeAutomaticObSource(automaticCarrySource, automaticObManualAmount)];
        }
      }

      // Varje aktiv OB-källa räknas separat. Exempel:
      // 3 timmar vanligt OB + 3 timmar helg-OB = 6 OB-timmar.
      activeSources.forEach((source) => {
        obMinutes += 1;
        obValue += Number(source.amount || 0) / 60;
      });
    }
  });

  return {
    hours: obMinutes / 60,
    value: obValue,
  };
}

function getDateKey(value) {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);

  const parsed = safeParseDate(value);
  return parsed ? formatDateKey(parsed) : "";
}

function sourceAppliesToMinute({
  dateKey,
  previousDateKey,
  minute,
  start,
  end,
  sourceDateKey,
}) {
  if (start <= end) {
    return sourceDateKey === dateKey && minute >= start && minute < end;
  }

  return (
    (sourceDateKey === dateKey && minute >= start && minute < 1440) ||
    (sourceDateKey === previousDateKey && minute >= 0 && minute < end)
  );
}

function getRuleDays(rule) {
  const rawDays =
    rule?.days ||
    rule?.selectedDays ||
    rule?.weekdays ||
    rule?.selectedWeekdays ||
    [];

  return Array.isArray(rawDays) ? rawDays.map(normalizeDayName) : [];
}

function getActiveObSourcesAtMinute(minute, date, obRules, holidays, customObEntries = []) {
  const sources = [];

  const dateKey = formatDateKey(date);
  const previousDate = addDays(date, -1);
  const previousDateKey = formatDateKey(previousDate);

  const currentWeekday = getSwedishWeekday(date);
  const previousWeekday = getSwedishWeekday(previousDate);

  (obRules || []).forEach((rule) => {
    const ruleName = rule?.name || rule?.title || "OB";

    // Datumspecifika OB-regler måste läsas från varje specificDates-post.
    // Varje post blir en egen OB-källa och kan därför överlappa andra OB-regler.
    if (rule?.useSpecificDates) {
      const specificDates = Array.isArray(rule?.specificDates)
        ? rule.specificDates
        : [];

      specificDates.forEach((item, index) => {
        const amount = Number(item?.amount ?? rule?.amount ?? 0);
        if (amount <= 0) return;

        const sourceDateKey = getDateKey(item?.date);
        const start = timeToMinutes(
          item?.startTime || rule?.startTime || "00:00"
        );
        const end = timeToMinutes(
          item?.endTime || rule?.endTime || "23:59"
        );

        if (
          sourceAppliesToMinute({
            dateKey,
            previousDateKey,
            minute,
            start,
            end,
            sourceDateKey,
          })
        ) {
          sources.push({
            name: item?.name || ruleName,
            amount,
            type: "obRule",
            sourceId: `${rule?.id || ruleName}__specific__${index}`,
          });
        }
      });

      return;
    }

    const amount = Number(
      rule?.amount ??
      rule?.obAmount ??
      rule?.value ??
      0
    );

    if (amount <= 0) return;

    const start = timeToMinutes(rule?.startTime || rule?.from || "00:00");
    const end = timeToMinutes(rule?.endTime || rule?.to || "23:59");
    const days = getRuleDays(rule);

    const appliesToday = days.length === 0 || days.includes(currentWeekday);
    const appliesPreviousDay =
      days.length === 0 || days.includes(previousWeekday);

    if (start <= end) {
      if (appliesToday && minute >= start && minute < end) {
        sources.push({
          name: ruleName,
          amount,
          type: "obRule",
          sourceId: rule?.id || ruleName,
        });
      }
    } else {
      if (appliesToday && minute >= start && minute < 1440) {
        sources.push({
          name: ruleName,
          amount,
          type: "obRule",
          sourceId: rule?.id || ruleName,
        });
      }

      if (appliesPreviousDay && minute >= 0 && minute < end) {
        sources.push({
          name: ruleName,
          amount,
          type: "obRule",
          sourceId: rule?.id || ruleName,
        });
      }
    }
  });

  (holidays || []).forEach((holiday) => {
    const amount = Number(holiday?.amount || 0);
    if (amount <= 0) return;

    const holidayDateKey = getDateKey(holiday?.date);
    const start = timeToMinutes(holiday?.startTime || "00:00");
    const end = timeToMinutes(holiday?.endTime || "23:59");

    if (
      sourceAppliesToMinute({
        dateKey,
        previousDateKey,
        minute,
        start,
        end,
        sourceDateKey: holidayDateKey,
      })
    ) {
      sources.push({
        name: holiday?.name || "Helgdag",
        amount,
        type: "holiday",
        sourceId: holiday?.id || holidayDateKey,
      });
    }
  });

  (customObEntries || []).forEach((customOb) => {
    const amount = Number(customOb?.amount || 0);
    if (amount <= 0) return;

    const name = customOb?.name || customOb?.description || "Eget OB-tillägg";
    const customDateKey = getDateKey(customOb?.date);
    const start = timeToMinutes(customOb?.startTime || "00:00");
    const end = timeToMinutes(customOb?.endTime || "23:59");

    if (
      sourceAppliesToMinute({
        dateKey,
        previousDateKey,
        minute,
        start,
        end,
        sourceDateKey: customDateKey,
      })
    ) {
      sources.push({
        name,
        amount,
        type: "custom",
        customObId: customOb.id,
        sourceId: customOb?.id || `${customDateKey}__${name}`,
      });
    }
  });

  return sources;
}
function mergeConsecutiveObDetails(obDetails) {
  if (!obDetails || obDetails.length === 0) return [];

  const datesAreConsecutive = (firstDateKey, secondDateKey) => {
    if (!firstDateKey || !secondDateKey) return false;
    const firstDate = new Date(`${firstDateKey}T12:00:00`);
    const secondDate = new Date(`${secondDateKey}T12:00:00`);
    if (Number.isNaN(firstDate.getTime()) || Number.isNaN(secondDate.getTime())) return false;
    firstDate.setDate(firstDate.getDate() + 1);
    return formatDateKey(firstDate) === secondDateKey;
  };
  
  const merged = [];
  let current = { ...obDetails[0] };
  
  for (let i = 1; i < obDetails.length; i++) {
    const next = obDetails[i];
    
    const sameDate = current.dateKey === next.dateKey;
    const continuesAcrossMidnight =
      current.sourceType === "automatic" &&
      next.sourceType === "automatic" &&
      current.endTime === "24:00" &&
      next.startTime === "00:00" &&
      datesAreConsecutive(current.dateKey, next.dateKey);

    const isSameType = current.type === next.type &&
                       current.rate === next.rate &&
                       current.sourceType === next.sourceType;

    const isConsecutive =
      (sameDate && current.endTime === next.startTime) ||
      continuesAcrossMidnight;
    
    if (isSameType && isConsecutive) {
      current.endTime = next.endTime;
      current.durationMinutes += next.durationMinutes;
      current.durationHours = current.durationMinutes / 60;
      current.value = (current.rate / 60) * current.durationMinutes;
    } else {
      merged.push(current);
      current = { ...next };
    }
  }
  merged.push(current);
  
  return merged;
}

function getAllObDetailsForShift(
  shift,
  obRules,
  holidays,
  employeeData = null,
  customObEntries = [],
  obCalculationMode = "combined",
  automaticObContinuation = false,
  automaticObManualAmount = null
) {
  if (!shift?.startTime || !shift?.endTime) {
    return [];
  }

  const shiftSegments = getShiftSegments(shift);
  const segments = getObTimelineSegments(shift, customObEntries);
  if (segments.length === 0) {
    return [];
  }

  const absenceIntervals = buildAbsenceIntervals(shiftSegments, employeeData);

  const isMinuteAbsent = (minute) => {
    return absenceIntervals.some(
      (interval) => minute >= interval.start && minute < interval.end
    );
  };

  const allObDetails = [];
  let automaticCarrySource = null;
  const canContinueAutomatically =
    automaticObContinuation && shiftCrossesMidnight(shift);

  segments.forEach((segment) => {
    const openIntervals = new Map();

    const makeKey = (source) => {
      return `${source.type || "ob"}__${source.name || "OB"}__${source.amount}__${source.sourceId || source.customObId || ""}`;
    };

    const closeInterval = (key, endMinute) => {
      const interval = openIntervals.get(key);
      if (!interval) return;

      const durationMinutes = endMinute - interval.startMinute;
      if (durationMinutes > 0) {
        allObDetails.push({
          type: interval.name,
          sourceType: interval.sourceType,
          rate: interval.amount,
          startTime: formatTimeFromMinutes(interval.startMinute),
          endTime: formatTimeFromMinutes(endMinute),
          durationMinutes,
          durationHours: durationMinutes / 60,
          value: (interval.amount / 60) * durationMinutes,
          customObId: interval.customObId,
          date: new Date(segment.date),
          dateKey: segment.dateKey,
        });
      }
      openIntervals.delete(key);
    };

    for (let minute = segment.startMinute; minute < segment.endMinute; minute++) {
      if (isMinuteAbsent(minute)) {
        Array.from(openIntervals.keys()).forEach((key) => {
          closeInterval(key, minute);
        });
        continue;
      }

      let activeSources = applyObCalculationMode(
        getActiveObSourcesAtMinute(
          minute,
          segment.date,
          obRules,
          holidays,
          customObEntries
        ),
        obCalculationMode
      );

      const minuteIsInsideShift = isMinuteInsideShift(
        shiftSegments,
        segment.dateKey,
        minute
      );

      // Utanför det ordinarie passet räknas endast den manuella
      // OB-förlängningen, inte vanliga OB-regler eller automatisk fortsättning.
      if (!minuteIsInsideShift) {
        activeSources = activeSources.filter((source) => source.type === "custom");
      } else if (canContinueAutomatically) {
        const latestSource = getAutomaticCarrySource(activeSources);

        if (latestSource) {
          automaticCarrySource = latestSource;
        } else if (activeSources.length === 0 && automaticCarrySource) {
          activeSources = [makeAutomaticObSource(automaticCarrySource, automaticObManualAmount)];
        }
      }

      const activeKeys = new Set(activeSources.map(makeKey));

      Array.from(openIntervals.keys()).forEach((key) => {
        if (!activeKeys.has(key)) {
          closeInterval(key, minute);
        }
      });

      activeSources.forEach((source) => {
        const key = makeKey(source);
        if (!openIntervals.has(key)) {
          openIntervals.set(key, {
            name: source.name,
            sourceType: source.type,
            amount: source.amount,
            startMinute: minute,
            customObId: source.customObId,
          });
        }
      });
    }

    Array.from(openIntervals.keys()).forEach((key) => {
      closeInterval(key, segment.endMinute);
    });
  });

  allObDetails.sort((a, b) => {
    const dateDiff = String(a.dateKey || "").localeCompare(String(b.dateKey || ""));
    if (dateDiff !== 0) return dateDiff;

    const aStart = timeToMinutes(a.startTime);
    const bStart = timeToMinutes(b.startTime);
    if (aStart !== bStart) return aStart - bStart;
    
    if (a.sourceType === "holiday" && b.sourceType !== "holiday") return -1;
    if (a.sourceType !== "holiday" && b.sourceType === "holiday") return 1;
    
    return String(a.type).localeCompare(String(b.type), "sv");
  });

  return mergeConsecutiveObDetails(allObDetails);
}

function normalizeId(value) {
  return String(value ?? "").trim();
}

function getEmployeeIdFromRequest(request, employeeList = []) {
  if (!request) return null;

  const directId =
    request?.employeeId ||
    request?.userId ||
    request?.uid ||
    request?.employee?.id ||
    request?.employee?.employeeId ||
    request?.user?.id ||
    null;

  if (directId) return normalizeId(directId);

  const email = String(
    request?.userEmail ||
      request?.employeeEmail ||
      request?.email ||
      request?.employee?.email ||
      ""
  )
    .trim()
    .toLowerCase();

  if (!email) return null;

  const foundEmployee = employeeList.find(
    (item) => String(item?.email || "").trim().toLowerCase() === email
  );

  return foundEmployee?.id ? normalizeId(foundEmployee.id) : null;
}

function getShiftIdFromRequest(request) {
  return normalizeId(
    request?.shiftId ||
      request?.originalShiftId ||
      request?.scheduleShiftId ||
      request?.linkedShiftId ||
      request?.shift?.id ||
      request?.shift?.shiftId ||
      ""
  );
}

function getRequestDateKey(request) {
  return getDateKey(
    request?.date ||
      request?.shiftDate ||
      request?.overtimeDate ||
      request?.requestedDate ||
      null
  );
}

function buildApprovedOvertimeEntry(request, overtimeRules = []) {
  const overtimeRuleId =
    request?.overtimeRuleId ||
    request?.overtimeTypeId ||
    request?.ruleId ||
    request?.typeId ||
    request?.overtime?.overtimeRuleId ||
    "";

  const matchingRule = overtimeRuleId
    ? overtimeRules.find(
        (rule) => normalizeId(rule?.id) === normalizeId(overtimeRuleId)
      )
    : null;

  const fallbackRule =
    matchingRule ||
    overtimeRules.find((rule) => Number(rule?.percentage || 0) > 0) ||
    null;

  const percentage = Number(
    request?.percentage ??
      request?.overtimePercentage ??
      request?.percent ??
      request?.ratePercent ??
      request?.overtime?.percentage ??
      fallbackRule?.percentage ??
      100
  );

  const minutes = Number(
    request?.minutes ??
      request?.overtimeMinutes ??
      request?.durationMinutes ??
      request?.approvedMinutes ??
      request?.overtime?.minutes ??
      0
  );

  const hours = Number(
    request?.hours ??
      request?.overtimeHours ??
      request?.durationHours ??
      request?.approvedHours ??
      request?.totalHours ??
      request?.overtime?.hours ??
      (minutes > 0 ? minutes / 60 : 0)
  );

  return {
    overtimeRequestId: request?.id,
    startTime:
      request?.startTime ||
      request?.fromTime ||
      request?.overtimeStartTime ||
      request?.overtime?.startTime ||
      "",
    endTime:
      request?.endTime ||
      request?.toTime ||
      request?.overtimeEndTime ||
      request?.overtime?.endTime ||
      "",
    appliesToWholeShift: Boolean(
      request?.appliesToWholeShift ??
        request?.wholeShift ??
        request?.isWholeShift ??
        request?.overtime?.appliesToWholeShift
    ),
    overtimeRuleId:
      overtimeRuleId || fallbackRule?.id || "approved-request",
    overtimeRuleName:
      request?.overtimeRuleName ||
      request?.overtimeTypeName ||
      request?.ruleName ||
      request?.typeName ||
      request?.overtime?.overtimeRuleName ||
      fallbackRule?.name ||
      "Godkänd övertid",
    percentage,
    hours,
    approvedFromRequest: true,
  };
}

function buildApprovedAbsenceEntry(request) {
  return {
    absenceRequestId: request.id,
    startTime: request.startTime || "",
    endTime: request.endTime || "",
    appliesToWholeShift: Boolean(request.appliesToWholeShift),
    absenceTypeId: request.absenceTypeId || "approved-request",
    absenceTypeName: request.absenceTypeName || "Godkänd frånvaro",
    type: request.absenceTypeName || request.type || "Godkänd frånvaro",
    absencePercent: Number(request.absencePercent ?? request.salaryPercent ?? 0),
    salaryPercent: Number(request.absencePercent ?? request.salaryPercent ?? 0),
    hours: Number(request.hours || 0),
    approvedFromRequest: true,
  };
}

function mergeApprovedRequestsForEmployee({
  shift,
  employeeId,
  employeeData,
  approvedOvertimeRequests,
  approvedAbsenceRequests,
  employees,
  overtimeRules,
}) {
  const shiftId = shift?.originalId || shift?.id;
  if (!shiftId || !employeeId) return employeeData;

  const baseOvertime = Array.isArray(employeeData?.overtime)
    ? employeeData.overtime.filter((entry) => !entry?.approvedFromRequest)
    : [];

  const baseAbsences = Array.isArray(employeeData?.absences)
    ? employeeData.absences.filter((entry) => !entry?.approvedFromRequest)
    : [];

  const shiftDateKey = getDateKey(shift?.date);

  const matchingOvertimeRequests = (approvedOvertimeRequests || []).filter((request) => {
    const requestEmployeeId = getEmployeeIdFromRequest(request, employees);
    if (normalizeId(requestEmployeeId) !== normalizeId(employeeId)) return false;

    const requestShiftId = getShiftIdFromRequest(request);
    if (requestShiftId) {
      return requestShiftId === normalizeId(shiftId);
    }

    const requestDateKey = getRequestDateKey(request);
    return Boolean(requestDateKey && shiftDateKey && requestDateKey === shiftDateKey);
  });

  const matchingAbsenceRequests = (approvedAbsenceRequests || []).filter((request) => {
    const requestShiftId = request?.shiftId || request?.originalShiftId;
    if (requestShiftId !== shiftId) return false;

    const requestEmployeeId = getEmployeeIdFromRequest(request, employees);
    return requestEmployeeId === employeeId;
  });

  return {
    ...employeeData,
    overtime: [
      ...baseOvertime,
      ...matchingOvertimeRequests.map((request) =>
        buildApprovedOvertimeEntry(request, overtimeRules)
      ),
    ],
    absences: [
      ...baseAbsences,
      ...matchingAbsenceRequests.map(buildApprovedAbsenceEntry),
    ],
  };
}

function buildEmployeeEntriesFromShift(
  shift,
  bookings = [],
  approvedOvertimeRequests = [],
  approvedAbsenceRequests = [],
  employees = [],
  overtimeRules = []
) {
  const entries = [];
  const addedEmployeeIds = new Set();

  const addEntry = (employeeId, employeeData = {}) => {
    const normalizedEmployeeId = employeeId || null;
    const isObemannad = Boolean(employeeData?.isObemannad);

    if (!normalizedEmployeeId || isObemannad) return;
    if (addedEmployeeIds.has(normalizedEmployeeId)) return;

    addedEmployeeIds.add(normalizedEmployeeId);

    const baseEmployeeData = {
      breaks: employeeData?.breaks || [],
      overtime: employeeData?.overtime || [],
      absences: employeeData?.absences || [],
      ...employeeData,
    };

    const employeeDataWithRequests = mergeApprovedRequestsForEmployee({
      shift,
      employeeId: normalizedEmployeeId,
      employeeData: baseEmployeeData,
      approvedOvertimeRequests,
      approvedAbsenceRequests,
      employees,
      overtimeRules,
    });

    entries.push({
      employeeId: normalizedEmployeeId,
      employeeData: employeeDataWithRequests,
    });
  };

  if (Array.isArray(shift?.employees)) {
    shift.employees.forEach((employeeData) => {
      addEntry(employeeData?.employeeId, employeeData);
    });
  } else {
    addEntry(shift?.employeeId, {
      payrollTime: shift?.payrollTime || null,
      breaks: shift?.breaks || [],
      overtime: shift?.overtime || [],
      absences: shift?.absences || [],
      employeeId: shift?.employeeId || null,
      isObemannad: shift?.isObemannad || false,
      name: shift?.name || shift?.employee || shift?.userName || "",
    });
  }

  const shiftIdentifier = shift?.originalId || shift?.id;

  bookings
    .filter((booking) => {
      const bookingShiftId = booking?.originalShiftId;
      return (
        booking?.status === "approved" &&
        booking?.userId &&
        bookingShiftId &&
        bookingShiftId === shiftIdentifier
      );
    })
    .forEach((booking) => {
      addEntry(booking.userId, {
        employeeId: booking.userId,
        name: booking.userName || "",
        breaks: [],
        overtime: [],
        absences: [],
        bookingId: booking.id,
        isFromBooking: true,
      });
    });

  return entries;
}

function getEmployeeShiftEntry(
  shift,
  employeeId,
  bookings,
  approvedOvertimeRequests,
  approvedAbsenceRequests,
  employees,
  overtimeRules
) {
  const entries = buildEmployeeEntriesFromShift(
    shift,
    bookings,
    approvedOvertimeRequests,
    approvedAbsenceRequests,
    employees,
    overtimeRules
  );
  return entries.find((entry) => entry.employeeId === employeeId) || null;
}

function isSickAbsence(employeeData) {
  const absences = Array.isArray(employeeData?.absences) ? employeeData.absences : [];
  return absences.some((item) => {
    const text = `${
      item?.absenceTypeName ||
      item?.typeName ||
      item?.name ||
      item?.reason ||
      item?.type ||
      ""
    }`
      .toLowerCase()
      .trim();
    return text.includes("sjuk");
  });
}

function hasSelectedSalaryType(employee) {
  if (!employee) return false;
  if (Object.prototype.hasOwnProperty.call(employee, "draftSalaryTypeId")) {
    return Boolean(employee.draftSalaryTypeId);
  }
  return Boolean(employee.savedSalaryTypeId);
}

function getHourlyRate(employee) {
  if (!hasSelectedSalaryType(employee)) return 0;
  return Number(employee?.draftPayAmount || employee?.savedPayAmount || 0);
}

function getPayType(employee) {
  return employee?.draftPayType || employee?.savedPayType || "hourly";
}

function getSemPercent(employee) {
  return Number(employee?.semProcent || employee?.savedSemProcent || 100);
}

function calculateMonthlyBasePay(employee, monthPaidHours) {
  if (!hasSelectedSalaryType(employee)) return 0;
  const payType = getPayType(employee);
  const payAmount = Number(employee?.draftPayAmount || employee?.savedPayAmount || 0);

  if (payType === "monthly") {
    return payAmount;
  }

  return payAmount * Number(monthPaidHours || 0);
}

function calculateVacationPayFromBase(basePay, employee) {
  if (!hasSelectedSalaryType(employee) || !employee?.semEnabled) return 0;
  const semPercent = getSemPercent(employee);
  return basePay * (VACATION_PAY_PERCENT / 100) * (semPercent / 100);
}

function getProjectName(projectIdOrName, projects) {
  if (!projectIdOrName) return "-";

  const project = projects.find((p) => p.id === projectIdOrName);
  if (project) return project.name;

  if (typeof projectIdOrName === "string") return projectIdOrName;

  return "-";
}

function buildRowsFromShifts(
  shifts,
  employee,
  obRules,
  holidays,
  bookings,
  currentMonth,
  projects,
  approvedOvertimeRequests,
  approvedAbsenceRequests,
  employees,
  overtimeRules,
  customObEntries,
  obCalculationMode = "combined",
  automaticObContinuation = false,
  automaticObManualAmount = null
) {
  if (!employee?.id) return [];

  const month = currentMonth.getMonth();
  const year = currentMonth.getFullYear();
  const hasSalaryType = hasSelectedSalaryType(employee);
  const payType = getPayType(employee);
  const hourlyRate = getHourlyRate(employee);

  const preliminaryRows = shifts
    .map((shift) => {
      const date = safeParseDate(shift.date);
      if (!date) return null;
      if (date.getMonth() !== month || date.getFullYear() !== year) return null;

      const employeeEntry = getEmployeeShiftEntry(
        shift,
        employee.id,
        bookings,
        approvedOvertimeRequests,
        approvedAbsenceRequests,
        employees,
        overtimeRules
      );
      if (!employeeEntry) return null;

      const scheduledStartTime = employeeEntry.employeeData?.payrollTime?.scheduledStartTime || shift.startTime;
      const scheduledEndTime = employeeEntry.employeeData?.payrollTime?.scheduledEndTime || shift.endTime;
      let employeeData = employeeEntry.employeeData || {};
      ({ shift, employeeData } = resolvePayrollTime(shift, employeeData));
      const totalShiftMinutes = calculateTotalShiftMinutes(shift);
      const breakMinutes = (() => {
        let minutes = calculateTotalBreakMinutes(employeeData);
        if (employeeData.breaks && Array.isArray(employeeData.breaks)) {
          employeeData.breaks.forEach((breakItem) => {
            if (breakItem.appliesToWholeShift) {
              const totalHours = totalShiftMinutes / 60;
              if (totalHours >= 5 && totalHours < 8) {
                minutes = Math.max(minutes, 30);
              } else if (totalHours >= 8) {
                minutes = Math.max(minutes, 60);
              }
            }
          });
        }
        return minutes;
      })();

      const workedHoursWithoutOvertime = calculateShiftHours(shift, employeeData);
      const absence = calculateAbsenceForShift(shift, employeeData);
      const overtime = calculateOvertimeForShift(shift, employeeData);
      const ob = calculateObForShift(
        shift,
        obRules,
        holidays,
        employee.obEnabled,
        employeeData,
        customObEntries,
        obCalculationMode,
        automaticObContinuation,
        automaticObManualAmount
      );

      const obDetails = getAllObDetailsForShift(
        shift,
        obRules,
        holidays,
        employeeData,
        customObEntries,
        obCalculationMode,
        automaticObContinuation,
        automaticObManualAmount
      );

      const totalWorkedHours = workedHoursWithoutOvertime + overtime.hours;
      const workHours = workTimeHours(shift, employeeData);
      const breakHours = breakMinutes / 60;
      const absenceHours = absence.hours;
      const overtimeHours = overtime.hours;

      const overtimeValue = hasSalaryType ? overtimePay(overtime, payType, employee?.draftPayAmount || employee?.savedPayAmount || 0, employee) : 0;

      const payAmount = Number(employee?.draftPayAmount || employee?.savedPayAmount || 0);
      const absencePay = calculateAbsencePay(absence, payType, payAmount, employee);
      const absenceDeduction = hasSalaryType ? absencePay.deduction : 0;

      const baseGrossForRow =
        hasSalaryType && payType === "hourly"
          ? (totalWorkedHours + absencePay.paidHours) * hourlyRate
          : 0;

      const projectName = getProjectName(shift.projectId || shift.project, projects);

      return {
        id: shift.id,
        date,
        project: projectName,
        employeeData,
        workTime: workHours,
        breakTime: breakHours,
        absenceTime: absenceHours,
        paidAbsenceHours: absencePay.paidHours,
        overtimeTime: overtimeHours,
        ordinaryTime: workedHoursWithoutOvertime,
        workedTime: totalWorkedHours,
        baseGrossForRow,
        grossSalary: baseGrossForRow,
        breakDeduction: 0,
        overtimeAddition: overtimeValue,
        vacationPay: 0,
        absenceDeduction,
        obHours: ob.hours,
        obValue: hasSalaryType ? ob.value : 0,
        obDetails,
        totalCompensation: 0,
        isSick: isSickAbsence(employeeData),
        scheduledStartTime,
        scheduledEndTime,
        shiftStartTime: shift.startTime,
        shiftEndTime: shift.endTime,
        overtimePremiumValue: overtime.premiumValue,
      };
    })
    .filter(Boolean)
    .sort((a, b) => (a.date?.getTime() || 0) - (b.date?.getTime() || 0));

  const monthWorkedHours = preliminaryRows.reduce(
    (sum, row) => sum + Number(row.workedTime || 0),
    0
  );
  const monthScheduledHours = preliminaryRows.reduce(
    (sum, row) =>
      sum + Number(row.workedTime || 0) + Number(row.absenceTime || 0),
    0
  );
  const monthPaidAbsenceHours = preliminaryRows.reduce(
    (sum, row) => sum + Number(row.paidAbsenceHours || 0),
    0
  );

  const monthlyBasePay = calculateMonthlyBasePay(employee, monthWorkedHours + monthPaidAbsenceHours);
  const totalBaseForVacation = monthlyBasePay;
  const totalVacationPay = calculateVacationPayFromBase(totalBaseForVacation, employee);

  return preliminaryRows.map((row) => {
    let allocatedBase = row.baseGrossForRow;

    if (payType === "monthly") {
      allocatedBase =
        monthScheduledHours > 0
          ? (monthlyBasePay *
              (Number(row.workedTime || 0) + Number(row.absenceTime || 0))) /
            monthScheduledHours
          : 0;
    }

    const vacationWeight =
      payType === "monthly"
        ? (Number(row.workedTime || 0) + Number(row.absenceTime || 0)) /
          (monthScheduledHours || 1)
        : (Number(row.workedTime || 0) + Number(row.paidAbsenceHours || 0)) /
          (monthWorkedHours + monthPaidAbsenceHours || 1);
    const allocatedVacation = totalVacationPay * vacationWeight;

    const totalCompensation =
      allocatedBase +
      Number(row.obValue || 0) +
      Number(row.overtimeAddition || 0) +
      allocatedVacation -
      Number(row.absenceDeduction || 0);

    return {
      ...row,
      grossSalary: allocatedBase,
      vacationPay: allocatedVacation,
      totalCompensation,
    };
  });
}

function AddCustomObModal({ isOpen, onClose, onSave, shiftId, shiftDate, shiftStartTime, shiftEndTime, initialOb = null, existingCustomObs = [] }) {
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("07:00");
  const [date, setDate] = useState("");
  const [isNightShift, setIsNightShift] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    if (shiftDate) {
      setDate(formatDateKey(shiftDate));
    }

    // När åtgärdsknappen klickas bredvid en OB-rad fylls formuläret
    // automatiskt med den valda OB-postens uppgifter. Alla fält är redigerbara.
    if (initialOb) {
      setName(initialOb.type || initialOb.name || "");
      setAmount(String(initialOb.rate ?? initialOb.amount ?? ""));
      setStartTime(initialOb.startTime || shiftStartTime || "00:00");
      setEndTime(initialOb.endTime || shiftEndTime || "23:59");

      const start = timeToMinutes(initialOb.startTime || shiftStartTime || "00:00");
      const end = timeToMinutes(initialOb.endTime || shiftEndTime || "23:59");
      setIsNightShift(end < start || start >= 22 * 60 || end <= 6 * 60);
      return;
    }

    setName("");
    setAmount("");

    if (shiftStartTime && shiftEndTime) {
      const start = timeToMinutes(shiftStartTime);
      const end = timeToMinutes(shiftEndTime);
      const isNight = end < start || start >= 22 * 60 || end <= 6 * 60;
      setIsNightShift(isNight);

      if (isNight) {
        setStartTime("00:00");
        setEndTime("07:00");
      } else {
        setStartTime(shiftStartTime || "00:00");
        setEndTime(shiftEndTime || "23:59");
      }
    }
  }, [isOpen, shiftDate, shiftStartTime, shiftEndTime, initialOb]);

  const handleSubmit = () => {
    if (!name.trim() || !amount || !date) {
      alert("Fyll i namn, belopp och datum");
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      alert("Ange ett giltigt belopp (kr/h)");
      return;
    }

    const existing = existingCustomObs.find(ob => 
      ob.date === date && 
      ob.startTime === startTime && 
      ob.endTime === endTime
    );

    if (existing) {
      if (!window.confirm(`Det finns redan ett OB-tillägg för denna tid. Vill du ersätta det?`)) {
        return;
      }
    }

    onSave({
      name: name.trim(),
      amount: amountNum,
      startTime,
      endTime,
      date,
      shiftId: normalizeId(shiftId),
      crossesMidnight: timeToMinutes(endTime) <= timeToMinutes(startTime),
      endDate: formatDateKey(
        timeToMinutes(endTime) <= timeToMinutes(startTime)
          ? addDays(new Date(`${date}T12:00:00`), 1)
          : new Date(`${date}T12:00:00`)
      ),
    });
    handleClose();
  };

  const handleClose = () => {
    setName("");
    setAmount("");
    setStartTime(shiftStartTime || "00:00");
    setEndTime(shiftEndTime || "23:59");
    if (shiftDate) {
      setDate(formatDateKey(shiftDate));
    }
    setIsNightShift(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.5)",
      zIndex: 4000,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }} onClick={handleClose}>
      <div style={{
        background: "#fff",
        borderRadius: 12,
        padding: 24,
        width: "min(90vw, 500px)",
        maxHeight: "90vh",
        overflow: "auto",
      }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 20px 0", fontSize: 20, fontWeight: 700, textAlign: "center" }}>
          Förlänga/Redigera OB
        </h3>
        
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>Beskrivning *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="t.ex. OB-tillägg"
            style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db", fontSize: "14px", boxSizing: "border-box" }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>Belopp (kr/h) *</label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="t.ex. 150"
            step="1"
            style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db", fontSize: "14px", boxSizing: "border-box" }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>Datum *</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db", fontSize: "14px", boxSizing: "border-box" }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 6, fontWeight: 600, textAlign: "center" }}>Tidsintervall</label>
          <div style={{ 
            display: "flex", 
            gap: 12, 
            alignItems: "center",
            justifyContent: "center",
            width: "100%"
          }}>
            <div style={{ flex: "0 0 45%" }}>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                style={{ 
                  width: "100%", 
                  padding: "10px 12px", 
                  borderRadius: 6, 
                  border: "1px solid #d1d5db",
                  fontSize: "14px",
                  textAlign: "center",
                  boxSizing: "border-box"
                }}
              />
            </div>
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              color: "#6b7280",
              fontWeight: 700,
              fontSize: "18px",
              flex: "0 0 30px"
            }}>
              −
            </div>
            <div style={{ flex: "0 0 45%" }}>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                style={{ 
                  width: "100%", 
                  padding: "10px 12px", 
                  borderRadius: 6, 
                  border: "1px solid #d1d5db",
                  fontSize: "14px",
                  textAlign: "center",
                  boxSizing: "border-box"
                }}
              />
            </div>
          </div>
        </div>

        {isNightShift && (
          <div style={{
            fontSize: 12,
            color: "#0369a1",
            marginBottom: 16,
            padding: "10px 14px",
            background: "#f0f9ff",
            borderRadius: 4,
            border: "1px solid #bae6fd",
            textAlign: "center"
          }}>
            💡 OB-tillägget förlängs från den ordinarie sluttiden till den förlängda sluttiden.
          </div>
        )}

        <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 20 }}>
          <button
            onClick={handleClose}
            style={{ 
              padding: "10px 24px", 
              borderRadius: 6, 
              border: "1px solid #d1d5db", 
              background: "#fff", 
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: 600,
              minWidth: "100px"
            }}
          >
            Avbryt
          </button>
          <button
            onClick={handleSubmit}
            style={{ 
              padding: "10px 24px", 
              borderRadius: 6, 
              border: "none", 
              background: "#1677ff", 
              color: "#fff", 
              cursor: "pointer", 
              fontWeight: 600,
              fontSize: "14px",
              minWidth: "100px"
            }}
          >
            Lägg till
          </button>
        </div>
      </div>
    </div>
  );
}
function EditCustomObModal({ isOpen, onClose, onSave, onDelete, customOb }) {
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("23:59");
  const [date, setDate] = useState("");

  useEffect(() => {
    if (customOb) {
      setName(customOb.name || "");
      setAmount(String(customOb.amount || ""));
      setStartTime(customOb.startTime || "00:00");
      setEndTime(customOb.endTime || "23:59");
      setDate(customOb.date || "");
    }
  }, [customOb]);

  const handleSubmit = async () => {
    if (!name.trim() || !amount || !date) {
      alert("Fyll i namn, belopp och datum");
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      alert("Ange ett giltigt belopp (kr/h)");
      return;
    }

    try {
      await onSave({
        ...customOb,
        name: name.trim(),
        amount: amountNum,
        startTime,
        endTime,
        date,
      });
      onClose();
    } catch (error) {
      console.error("Fel vid sparande:", error);
      alert("Kunde inte spara ändringarna");
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Är du säker på att du vill ta bort detta OB-tillägg?")) {
      try {
        await onDelete(customOb.id);
        onClose();
      } catch (error) {
        console.error("Fel vid borttagning:", error);
        alert("Kunde inte ta bort OB-tillägget");
      }
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || !customOb) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 4000,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      onClick={handleOverlayClick}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: 24,
          width: "min(90vw, 500px)",
          maxHeight: "90vh",
          overflow: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: "0 0 20px 0", fontSize: 20, fontWeight: 700 }}>
          Redigera OB-tillägg
        </h3>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
            Beskrivning *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #d1d5db" }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
            Belopp (kr/h) *
          </label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            step="1"
            style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #d1d5db" }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
            Datum *
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #d1d5db" }}
          />
        </div>

        <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
              Starttid
            </label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #d1d5db" }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
              Sluttid
            </label>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #d1d5db" }}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, justifyContent: "space-between", marginTop: 20 }}>
          <button
            onClick={handleDelete}
            style={{
              padding: "8px 16px",
              borderRadius: 6,
              border: "1px solid #dc2626",
              background: "#fff",
              color: "#dc2626",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            <FaTrash style={{ marginRight: 6 }} /> Ta bort
          </button>
          <div style={{ display: "flex", gap: 12 }}>
            <button
              onClick={onClose}
              style={{
                padding: "8px 16px",
                borderRadius: 6,
                border: "1px solid #d1d5db",
                background: "#fff",
                cursor: "pointer",
              }}
            >
              Avbryt
            </button>
            <button
              onClick={handleSubmit}
              style={{
                padding: "8px 16px",
                borderRadius: 6,
                border: "none",
                background: "#1677ff",
                color: "#fff",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              <FaSave style={{ marginRight: 6 }} /> Spara
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AutomaticObAmountModal({
  isOpen,
  onClose,
  onSave,
  currentManualAmount = null,
}) {
  const [mode, setMode] = useState("automatic");
  const [amount, setAmount] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    const hasManualAmount = Number(currentManualAmount) > 0;
    setMode(hasManualAmount ? "manual" : "automatic");
    setAmount(hasManualAmount ? String(currentManualAmount) : "");
  }, [isOpen, currentManualAmount]);

  if (!isOpen) return null;

  const handleSave = () => {
    if (mode === "manual") {
      const parsedAmount = Number(amount);
      if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
        alert("Ange ett giltigt OB-belopp i kr/h");
        return;
      }
      onSave(parsedAmount);
      return;
    }

    onSave(null);
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        zIndex: 4100,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "min(92vw, 480px)",
          background: "#fff",
          borderRadius: 14,
          padding: 24,
          boxShadow: "0 20px 50px rgba(15, 23, 42, 0.25)",
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <h3 style={{ margin: "0 0 8px", fontSize: 20 }}>
          Ändra automatiskt OB
        </h3>
        <p style={{ margin: "0 0 20px", color: "#64748b", fontSize: 13, lineHeight: 1.5 }}>
          Inställningen påverkar endast automatiskt OB efter midnatt.
        </p>

        <button
          type="button"
          onClick={() => setMode("automatic")}
          style={{
            width: "100%",
            textAlign: "left",
            padding: 14,
            marginBottom: 10,
            borderRadius: 10,
            border: mode === "automatic" ? "2px solid #7c3aed" : "1px solid #cbd5e1",
            background: mode === "automatic" ? "#f5f3ff" : "#fff",
            cursor: "pointer",
          }}
        >
          <strong>Automatiskt – följ senaste OB</strong>
          <div style={{ marginTop: 4, color: "#64748b", fontSize: 12 }}>
            Samma funktion som nu: den senaste aktiva OB-summan fortsätter efter midnatt.
          </div>
        </button>

        <button
          type="button"
          onClick={() => setMode("manual")}
          style={{
            width: "100%",
            textAlign: "left",
            padding: 14,
            borderRadius: 10,
            border: mode === "manual" ? "2px solid #2563eb" : "1px solid #cbd5e1",
            background: mode === "manual" ? "#eff6ff" : "#fff",
            cursor: "pointer",
          }}
        >
          <strong>Ändra manuellt summa</strong>
          <div style={{ marginTop: 4, color: "#64748b", fontSize: 12 }}>
            Ange ett fast kr/h-belopp för automatiskt OB efter midnatt.
          </div>
        </button>

        {mode === "manual" && (
          <div style={{ marginTop: 14 }}>
            <label style={{ display: "block", marginBottom: 6, fontWeight: 700 }}>
              Automatiskt OB efter midnatt (kr/h)
            </label>
            <input
              type="number"
              min="0"
              step="1"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder="Exempel: 75"
              autoFocus
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #94a3b8",
                fontSize: 15,
              }}
            />
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 22 }}>
          <button
            type="button"
            onClick={onClose}
            style={{ padding: "9px 16px", borderRadius: 7, border: "1px solid #cbd5e1", background: "#fff", cursor: "pointer" }}
          >
            Avbryt
          </button>
          <button
            type="button"
            onClick={handleSave}
            style={{ padding: "9px 16px", borderRadius: 7, border: "none", background: "#1677ff", color: "#fff", fontWeight: 700, cursor: "pointer" }}
          >
            Spara
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TimeReportPopup({
  isOpen,
  onClose,
  employee,
  companyRootUid,
  initialMonth,
  backLabel = "Tillbaka till lönhantering",
}) {
  const [activeTab, setActiveTab] = useState("time");
  const [showObSettings, setShowObSettings] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const date = safeParseDate(initialMonth) || new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
  });
  const [reportApproval, setReportApproval] = useState(null);
  const reportApprovalKey = `${companyRootUid}_${employee?.id}_${currentMonth.getFullYear()}-${pad(currentMonth.getMonth() + 1)}`;

  useEffect(() => {
    if (!isOpen || !companyRootUid || !employee?.id) return;
    const period = `${currentMonth.getFullYear()}-${pad(currentMonth.getMonth() + 1)}`;
    const approvalRef = doc(db, "users", companyRootUid, "payrollApprovals", `${period}_${employee.id}`);
    return onSnapshot(approvalRef, (snapshot) => {
      setReportApproval({ key: reportApprovalKey, data: snapshot.exists() ? snapshot.data() : null });
    }, (error) => {
      console.error("Kunde inte läsa rapportens godkännande:", error);
      setReportApproval(null);
    });
  }, [isOpen, companyRootUid, employee?.id, currentMonth, reportApprovalKey]);

  const approval = reportApproval?.key === reportApprovalKey ? reportApproval.data : null;
  const obEditingDisabled = reportApproval?.key !== reportApprovalKey || approval?.approved === true;
  const approvedAt = safeParseDate(approval?.approvedAt);
  const shortMonths = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
  const approvedAtLabel = approvedAt
    ? `${approvedAt.getDate()} ${shortMonths[approvedAt.getMonth()]} ${approvedAt.getFullYear()} ${pad(approvedAt.getHours())}:${pad(approvedAt.getMinutes())}`
    : "";

  const [allShifts, setAllShifts] = useState([]);
  const [obRules, setObRules] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [allProjects, setProjects] = useState([]);
  const projects = useVisibleProjects(allProjects);
  const [employees, setEmployees] = useState([]);
  const [overtimeRules, setOvertimeRules] = useState([]);
  const [approvedOvertimeRequests, setApprovedOvertimeRequests] = useState([]);
  const [approvedAbsenceRequests, setApprovedAbsenceRequests] = useState([]);
  const [customObEntries, setCustomObEntries] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCustomOb, setSelectedCustomOb] = useState(null);
  const [selectedShiftForOb, setSelectedShiftForOb] = useState(null);
  const [obCalculationMode, setObCalculationMode] = useState("combined");
  const [savedObCalculationMode, setSavedObCalculationMode] = useState("combined");
  const [isSavingObMode, setIsSavingObMode] = useState(false);
  const [obModeSaveMessage, setObModeSaveMessage] = useState("");
  const [automaticObContinuation, setAutomaticObContinuation] = useState(false);
  const [savedAutomaticObContinuation, setSavedAutomaticObContinuation] = useState(false);
  const [automaticObManualAmount, setAutomaticObManualAmount] = useState(null);
  const [isAutomaticObAmountModalOpen, setIsAutomaticObAmountModalOpen] = useState(false);

  useEffect(() => {
    if (!obEditingDisabled) return;
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedShiftForOb(null);
    setSelectedCustomOb(null);
  }, [obEditingDisabled]);

  useEffect(() => {
    if (!isOpen || !companyRootUid) return;

    const customObRef = collection(db, "users", companyRootUid, "customObEntries");
    
    const unsubscribe = onSnapshot(
      customObRef,
      (snapshot) => {
        const entries = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        const filteredEntries = entries.filter(entry => entry.employeeId === employee?.id);
        setCustomObEntries(filteredEntries);
      },
      (error) => {
        console.error("Fel vid hämtning av egna OB-tillägg:", error);
        setCustomObEntries([]);
      }
    );

    return () => unsubscribe();
  }, [isOpen, companyRootUid, employee?.id]);

  useEffect(() => {
    if (!isOpen || !companyRootUid || !employee?.id) return;

    const monthKey = `${currentMonth.getFullYear()}-${pad(currentMonth.getMonth() + 1)}`;
    const settingId = `${employee.id}_${monthKey}`;
    const settingRef = doc(
      db,
      "users",
      companyRootUid,
      "salaryReportSettings",
      settingId
    );

    const unsubscribe = onSnapshot(
      settingRef,
      (snapshot) => {
        const savedMode = snapshot.exists()
          ? snapshot.data()?.obCalculationMode
          : "combined";
        const validMode = savedMode === "holidayPriority" ? "holidayPriority" : "combined";
        const savedAutomatic = snapshot.exists()
          ? Boolean(snapshot.data()?.automaticObContinuation)
          : false;
        setObCalculationMode(validMode);
        setSavedObCalculationMode(validMode);
        setAutomaticObContinuation(savedAutomatic);
        setSavedAutomaticObContinuation(savedAutomatic);
        const savedManualAmount = Number(snapshot.data()?.automaticObManualAmount);
        setAutomaticObManualAmount(
          Number.isFinite(savedManualAmount) && savedManualAmount > 0
            ? savedManualAmount
            : null
        );
        setObModeSaveMessage("");
      },
      (error) => {
        console.error("Fel vid hämtning av OB-val för lönerapport:", error);
        setObCalculationMode("combined");
        setSavedObCalculationMode("combined");
        setAutomaticObContinuation(false);
        setSavedAutomaticObContinuation(false);
        setAutomaticObManualAmount(null);
      }
    );

    return () => unsubscribe();
  }, [isOpen, companyRootUid, employee?.id, currentMonth]);

  useEffect(() => {
    if (!isOpen || !companyRootUid) return;

    const unsubscribers = [];

    unsubscribers.push(
      onSnapshot(
        collection(db, "users", companyRootUid, "projects"),
        (snapshot) => {
          const projectList = [];
          snapshot.forEach((docSnap) => {
            const data = docSnap.data();
            projectList.push({
              id: docSnap.id,
              name: data.name || "Projekt saknas",
            });
          });
          setProjects(projectList);
        },
        (error) => {
          console.error("Fel vid hämtning av projekt:", error);
          setProjects([]);
        }
      )
    );

    unsubscribers.push(
      onSnapshot(
        collection(db, "users", companyRootUid, "shifts"),
        (snapshot) => {
          const items = snapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }));
          setAllShifts(items);
        },
        (error) => {
          console.error("Fel vid hämtning av tidsrapport:", error);
          setAllShifts([]);
        }
      )
    );

    unsubscribers.push(
      onSnapshot(
        collection(db, "users", companyRootUid, "obRules"),
        (snapshot) => {
          setObRules(
            snapshot.docs.map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data(),
            }))
          );
        },
        (error) => {
          console.error("Fel vid hämtning av OB-regler:", error);
          setObRules([]);
        }
      )
    );

    unsubscribers.push(
      onSnapshot(
        collection(db, "users", companyRootUid, "holidays"),
        (snapshot) => {
          setHolidays(
            snapshot.docs.map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data(),
            }))
          );
        },
        (error) => {
          console.error("Fel vid hämtning av helgdagar:", error);
          setHolidays([]);
        }
      )
    );

    unsubscribers.push(
      onSnapshot(
        collection(db, "users", companyRootUid, "bookings"),
        (snapshot) => {
          setBookings(
            snapshot.docs.map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data(),
            }))
          );
        },
        (error) => {
          console.error("Fel vid hämtning av bokningar:", error);
          setBookings([]);
        }
      )
    );

    unsubscribers.push(
      onSnapshot(
        collection(db, "users", companyRootUid, "employed"),
        (snapshot) => {
          const userList = snapshot.docs.map((docSnap) => {
            const data = docSnap.data();
            let fullName = data.name || "";
            if (data.firstName && data.lastName) {
              fullName = `${data.firstName} ${data.lastName}`;
            } else if (data.firstName) {
              fullName = data.firstName;
            } else if (data.lastName) {
              fullName = data.lastName;
            } else if (data.displayName) {
              fullName = data.displayName;
            } else if (data.userName) {
              fullName = data.userName;
            }

            return {
              id: docSnap.id,
              ...data,
              name: fullName || "Användare",
              email: data.email || "",
            };
          });

          setEmployees(userList);
        },
        (error) => {
          console.error("Fel vid hämtning av anställda:", error);
          setEmployees([]);
        }
      )
    );

    unsubscribers.push(
      onSnapshot(
        collection(db, "users", companyRootUid, "overtimeRules"),
        (snapshot) => {
          const rules = snapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }));
          setOvertimeRules(rules);
        },
        (error) => {
          console.error("Fel vid hämtning av övertidsregler:", error);
          setOvertimeRules([]);
        }
      )
    );

    unsubscribers.push(
      onSnapshot(
        query(
          collection(db, "users", companyRootUid, "overtimeRequests"),
          where("status", "==", "approved")
        ),
        (snapshot) => {
          setApprovedOvertimeRequests(
            snapshot.docs.map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data(),
            }))
          );
        },
        (error) => {
          console.error("Fel vid hämtning av godkända övertidsansökningar:", error);
          setApprovedOvertimeRequests([]);
        }
      )
    );

    unsubscribers.push(
      onSnapshot(
        query(
          collection(db, "users", companyRootUid, "absenceRequests"),
          where("status", "==", "approved")
        ),
        (snapshot) => {
          setApprovedAbsenceRequests(
            snapshot.docs.map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data(),
            }))
          );
        },
        (error) => {
          console.error("Fel vid hämtning av godkända frånvaroansökningar:", error);
          setApprovedAbsenceRequests([]);
        }
      )
    );

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe && unsubscribe());
    };
  }, [isOpen, companyRootUid]);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab("time");
    }
  }, [isOpen]);

  const employeeListForMatching = useMemo(() => {
    if (!employee?.id) return employees;
    const exists = employees.some((emp) => emp.id === employee.id);
    return exists ? employees : [...employees, employee];
  }, [employees, employee]);

  const monthRows = useMemo(() => {
    return buildRowsFromShifts(
      allShifts,
      employee,
      obRules,
      holidays,
      bookings,
      currentMonth,
      projects,
      approvedOvertimeRequests,
      approvedAbsenceRequests,
      employeeListForMatching,
      overtimeRules,
      customObEntries,
      obCalculationMode,
      automaticObContinuation,
      automaticObManualAmount
    );
  }, [
    allShifts,
    employee,
    obRules,
    holidays,
    bookings,
    currentMonth,
    projects,
    approvedOvertimeRequests,
    approvedAbsenceRequests,
    employeeListForMatching,
    overtimeRules,
    customObEntries,
    obCalculationMode,
    automaticObContinuation,
    automaticObManualAmount,
  ]);

  const totals = useMemo(() => {
    return monthRows.reduce(
      (acc, row) => {
        acc.workTime += Number(row.workTime || 0);
        acc.breakTime += Number(row.breakTime || 0);
        acc.absenceTime += Number(row.absenceTime || 0);
        acc.overtimeTime += Number(row.overtimeTime || 0);
        acc.ordinaryTime += Number(row.ordinaryTime || 0);
        acc.workedTime += Number(row.workedTime || 0);
        acc.grossSalary += Number(row.grossSalary || 0);
        acc.breakDeduction += Number(row.breakDeduction || 0);
        acc.overtimeAddition += Number(row.overtimeAddition || 0);
        acc.vacationPay += Number(row.vacationPay || 0);
        acc.absenceDeduction += Number(row.absenceDeduction || 0);
        acc.obValue += Number(row.obValue || 0);
        acc.obHours += Number(row.obHours || 0);
        acc.totalCompensation += Number(row.totalCompensation || 0);
        return acc;
      },
      {
        workTime: 0,
        breakTime: 0,
        absenceTime: 0,
        overtimeTime: 0,
        ordinaryTime: 0,
        workedTime: 0,
        grossSalary: 0,
        breakDeduction: 0,
        overtimeAddition: 0,
        vacationPay: 0,
        absenceDeduction: 0,
        obValue: 0,
        obHours: 0,
        totalCompensation: 0,
      }
    );
  }, [monthRows]);

  // Samma procentbaserade skatteberäkning som i löneöversikten.
  const parsedTaxRate = Number(employee?.tax ?? employee?.payroll?.tax ?? 0);
  const taxRate = Number.isNaN(parsedTaxRate) ? 0 : Math.max(0, Math.min(100, parsedTaxRate));
  const grossSalaryTotal = totals.totalCompensation;
  const netSalaryTotal = grossSalaryTotal - grossSalaryTotal * (taxRate / 100);

  const sicknessTotal = useMemo(() => {
    return monthRows.reduce((sum, row) => {
      if (row.isSick) {
        return sum + Number(row.absenceTime || 0);
      }
      return sum;
    }, 0);
  }, [monthRows]);

  const handleAddCustomOb = async (newOb) => {
    if (obEditingDisabled) return;
    if (!companyRootUid || !employee?.id) return;

    try {
      const customObRef = collection(db, "users", companyRootUid, "customObEntries");
      await addDoc(customObRef, {
        ...newOb,
        employeeId: employee.id,
        employeeName: employee.name,
        createdAt: new Date().toISOString(),
        createdBy: employee.id,
      });
    } catch (error) {
      console.error("Fel vid sparande av OB-tillägg:", error);
      alert("Kunde inte spara OB-tillägget");
    }
  };

  const handleUpdateCustomOb = async (updatedOb) => {
    if (obEditingDisabled) return;
    if (!companyRootUid || !employee?.id || !updatedOb.id) return;

    try {
      const customObRef = doc(db, "users", companyRootUid, "customObEntries", updatedOb.id);
      await setDoc(customObRef, {
        ...updatedOb,
        employeeId: employee.id,
        updatedAt: new Date().toISOString(),
      }, { merge: true });
    } catch (error) {
      console.error("Fel vid uppdatering av OB-tillägg:", error);
      alert("Kunde inte uppdatera OB-tillägget");
    }
  };

  const handleDeleteCustomOb = async (id) => {
    if (obEditingDisabled) return;
    if (!companyRootUid || !employee?.id || !id) return;

    try {
      const customObRef = doc(db, "users", companyRootUid, "customObEntries", id);
      await deleteDoc(customObRef);
    } catch (error) {
      console.error("Fel vid borttagning av OB-tillägg:", error);
      alert("Kunde inte ta bort OB-tillägget");
    }
  };

  const handleSaveObCalculationMode = async () => {
    if (!companyRootUid || !employee?.id) return;

    const monthKey = `${currentMonth.getFullYear()}-${pad(currentMonth.getMonth() + 1)}`;
    const settingId = `${employee.id}_${monthKey}`;
    const settingRef = doc(
      db,
      "users",
      companyRootUid,
      "salaryReportSettings",
      settingId
    );

    try {
      setIsSavingObMode(true);
      setObModeSaveMessage("");

      await setDoc(
        settingRef,
        {
          employeeId: employee.id,
          employeeName: employee.name || "",
          monthKey,
          obCalculationMode,
          automaticObContinuation,
          automaticObManualAmount,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      setSavedObCalculationMode(obCalculationMode);
      setSavedAutomaticObContinuation(automaticObContinuation);
      setObModeSaveMessage("Sparat!");
    } catch (error) {
      console.error("Fel vid sparande av OB-val:", error);
      setObModeSaveMessage("Kunde inte spara");
      alert("Kunde inte spara valet mellan OB och Helg-OB");
    } finally {
      setIsSavingObMode(false);
    }
  };

  const handleSaveAutomaticObAmount = async (manualAmount) => {
    if (!companyRootUid || !employee?.id) return;

    const normalizedAmount = Number(manualAmount);
    const valueToSave =
      Number.isFinite(normalizedAmount) && normalizedAmount > 0
        ? normalizedAmount
        : null;

    const monthKey = `${currentMonth.getFullYear()}-${pad(currentMonth.getMonth() + 1)}`;
    const settingId = `${employee.id}_${monthKey}`;
    const settingRef = doc(
      db,
      "users",
      companyRootUid,
      "salaryReportSettings",
      settingId
    );

    try {
      await setDoc(
        settingRef,
        {
          employeeId: employee.id,
          employeeName: employee.name || "",
          monthKey,
          automaticObManualAmount: valueToSave,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
      setAutomaticObManualAmount(valueToSave);
      setIsAutomaticObAmountModalOpen(false);
    } catch (error) {
      console.error("Fel vid sparande av automatiskt OB-belopp:", error);
      alert("Kunde inte spara automatiskt OB-belopp");
    }
  };

  const handlePreviousMonth = () => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  if (!isOpen) return null;

  const styles = {
    page: {
      width: "100%",
      minWidth: 0,
      containerType: "inline-size",
      background: "#f7f7f8",
      boxSizing: "border-box",
    },
    content: {
      width: "100%",
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "20px 12px 12px",
      flexWrap: "wrap",
      gap: 16,
    },
    title: {
      margin: 0,
      fontSize: 24,
      overflowWrap: "anywhere",
      fontWeight: 800,
      color: "#111827",
    },
    monthNavInline: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      flexWrap: "wrap",
      marginLeft: "auto",
    },
    monthNavButton: {
      width: 36,
      height: 36,
      borderRadius: 8,
      border: "1px solid #d1d5db",
      background: "#fff",
      color: "#111827",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    monthLabel: {
      minWidth: 0,
      fontSize: 18,
      fontWeight: 700,
      color: "#4b5563",
      textAlign: "center",
    },
    topActions: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 14,
      padding: "4px 12px 12px",
      flexWrap: "wrap",
    },
    tabsWrap: {
      display: "flex",
      gap: 12,
    },
    tabButton: (active) => ({
      height: 38,
      padding: "0 16px",
      borderRadius: 8,
      border: active ? "1px solid #1677ff" : "1px solid #3b82f6",
      background: active ? "#1677ff" : "#fff",
      color: active ? "#fff" : "#2563eb",
      fontWeight: 700,
      fontSize: 14,
      cursor: "pointer",
    }),
    rightButtons: {
      display: "flex",
      gap: 12,
      flexWrap: "wrap",
      marginLeft: "auto",
    },
    secondaryButton: {
      height: 38,
      padding: "0 16px",
      borderRadius: 8,
      border: "1px solid #3b82f6",
      background: "#fff",
      color: "#2563eb",
      fontWeight: 700,
      fontSize: 14,
      cursor: "pointer",
    },
    body: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      padding: "0 12px 16px",
      minWidth: 0,
    },
    tableWrap: {
      border: "1px solid #d1d5db",
      background: "#fff",
      minWidth: 0,
      flex: 1,
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      tableLayout: "fixed",
    },
    salaryTable: {
      width: "100%",
      borderCollapse: "collapse",
      tableLayout: "fixed",
    },
    th: {
      background: "#f3f4f6",
      color: "#111827",
      fontWeight: 800,
      fontSize: 13,
      textAlign: "center",
      padding: "10px 8px",
      border: "1px solid #d1d5db",
      whiteSpace: "normal",
      overflowWrap: "anywhere",
    },
    td: {
      fontSize: 13,
      color: "#111827",
      padding: "10px 8px",
      border: "1px solid #e5e7eb",
      textAlign: "center",
      whiteSpace: "normal",
      overflowWrap: "anywhere",
    },
    summaryCell: {
      fontWeight: 800,
      fontSize: 14,
    },
    salaryFooter: {
      marginTop: 12,
      borderTop: "1px solid #d1d5db",
      paddingTop: 14,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-end",
      flexWrap: "wrap",
      gap: 16,
    },
    totalSalaryText: {
      fontSize: 16,
      fontWeight: 800,
      color: "#111827",
    },
    footer: {
      marginTop: 12,
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
      gap: 0,
      alignItems: "stretch",
    },
    statBox: {
      background: "#fff",
      border: "1px solid #d1d5db",
      minHeight: 70,
      padding: "6px 10px 8px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    },
    statHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      fontSize: 12,
      fontWeight: 800,
      color: "#111827",
    },
    statRow: {
      display: "flex",
      gap: 16,
      fontSize: 12,
      color: "#374151",
      fontWeight: 500,
      paddingTop: 6,
    },
    closeButtonWrap: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "flex-end",
      paddingLeft: 10,
    },
    closeButton: {
      height: 40,
      minWidth: 110,
      padding: "0 20px",
      borderRadius: 10,
      border: "1px solid #1677ff",
      background: "#1677ff",
      color: "#fff",
      fontSize: 16,
      fontWeight: 700,
      cursor: "pointer",
    },
    closeX: {
      width: 34,
      height: 34,
      borderRadius: 999,
      border: "1px solid #d1d5db",
      background: "#fff",
      color: "#374151",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
    },
  };

  return (
    <section className="time-report-page" style={styles.page} aria-label="Tids- och lönrapport">
      <div style={styles.content}>
        <div style={styles.header}>
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 16, minWidth: 0 }}>
            <h2 style={styles.title}>
              {activeTab === "time" ? "Tidsrapport" : "Lönrapport"} för {employee?.name || "Anställd"}
            </h2>
            <div className="report-approval" role="status">
              <div className="report-approval-heading">
                <span className="report-approval-label">Lönestatus</span>
                <span className={`report-approval-badge${approval?.approved === true ? "" : " report-approval-badge--pending"}`}>
                  <span className="report-approval-dot" aria-hidden="true" />
                  {reportApproval?.key !== reportApprovalKey
                    ? "Laddar status…"
                    : approval?.approved === true ? "Godkänd" : "Ej godkänd"}
                </span>
              </div>
              {approval?.approved === true && (
                <div className="report-approval-details">
                  <span>Godkänd av <strong>{approval.approvedByName || "Okänd användare"}</strong></span>
                  {approvedAtLabel && <span className="report-approval-date">{approvedAtLabel}</span>}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 12, marginLeft: "auto", minWidth: 0 }}>
            <div style={styles.monthNavInline}>
              <button style={styles.monthNavButton} onClick={handlePreviousMonth}>
                <FaChevronLeft size={12} />
              </button>
              <div style={styles.monthLabel}>{getMonthLabel(currentMonth)}</div>
              <button style={styles.monthNavButton} onClick={handleNextMonth}>
                <FaChevronRight size={12} />
              </button>
            </div>

          </div>

        </div>

        <div style={styles.topActions}>
          <button
            type="button"
            style={{ ...styles.closeButton, display: "inline-flex", alignItems: "center", gap: 8 }}
            onClick={onClose}
            aria-label={backLabel}
          >
            <FaChevronLeft size={12} />
            Tillbaka
          </button>

          {activeTab === "salary" && (
            <button
              type="button"
              style={styles.secondaryButton}
              aria-expanded={showObSettings}
              aria-controls="time-report-ob-settings"
              onClick={() => setShowObSettings((previous) => !previous)}
            >
              {showObSettings ? "Dölj OB-inställningar" : "OB-inställningar"}
            </button>
          )}

            <div style={{ ...styles.tabsWrap, marginLeft: "auto" }}>
              <button
                type="button"
                aria-pressed={activeTab === "time"}
                style={styles.tabButton(activeTab === "time")}
                onClick={() => setActiveTab("time")}
              >
                Tidsrapport
              </button>
              <button
                type="button"
                aria-pressed={activeTab === "salary"}
                style={styles.tabButton(activeTab === "salary")}
                onClick={() => setActiveTab("salary")}
              >
                Lönrapport
              </button>
            </div>

          {activeTab === "salary" && showObSettings && (
            <div
              id="time-report-ob-settings"
              role="region"
              aria-label="OB-inställningar"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid #dbe3ef",
                background: "#ffffff",
                flexWrap: "wrap",
                flex: "1 1 100%",
                minWidth: 0,
                boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 5,
                  paddingRight: 12,
                  borderRight: "1px solid #e2e8f0",
                }}
              >
                <span style={{ fontSize: 10, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Beräkning
                </span>
                <div style={{ display: "flex", gap: 4, padding: 3, borderRadius: 8, background: "#f1f5f9" }}>
                  {[
                    { id: "combined", label: "Räkna ihop OB", color: "#1677ff" },
                    { id: "holidayPriority", label: "Helg-OB har företräde", color: "#f59e0b" },
                  ].map((option) => {
                    const isActive = obCalculationMode === option.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => {
                          setObCalculationMode(option.id);
                          setObModeSaveMessage("");
                        }}
                        style={{
                          padding: "7px 11px",
                          borderRadius: 6,
                          border: "none",
                          background: isActive ? option.color : "transparent",
                          color: isActive ? "#ffffff" : "#475569",
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: "pointer",
                          whiteSpace: "normal",
      overflowWrap: "anywhere",
                        }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  flexWrap: "wrap",
                }}
              >
                <span style={{ width: "100%", fontSize: 10, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Automatik
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setAutomaticObContinuation((previous) => !previous);
                    setObModeSaveMessage("");
                  }}
                  title="Låt den senaste kvälls-OB:n fortsätta automatiskt när passet går över midnatt"
                  aria-pressed={automaticObContinuation}
                  style={{
                    padding: "8px 11px",
                    borderRadius: 999,
                    border: automaticObContinuation ? "1px solid #7c3aed" : "1px solid #cbd5e1",
                    background: automaticObContinuation ? "#7c3aed" : "#ffffff",
                    color: automaticObContinuation ? "#ffffff" : "#475569",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <FaMagic size={11} />
                  Automatiskt: {automaticObContinuation ? "På" : "Av"}
                </button>

                <button
                  type="button"
                  onClick={() => setIsAutomaticObAmountModalOpen(true)}
                  title="Ändra beloppet för alla automatiska OB-fortsättningar"
                  aria-label="Ändra automatiskt OB"
                  disabled={!automaticObContinuation}
                  style={{
                    padding: "8px 11px",
                    borderRadius: 7,
                    border: automaticObContinuation ? "1px solid #c4b5fd" : "1px solid #e2e8f0",
                    background: automaticObContinuation ? "#f5f3ff" : "#f8fafc",
                    color: automaticObContinuation ? "#6d28d9" : "#94a3b8",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: automaticObContinuation ? "pointer" : "not-allowed",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    whiteSpace: "normal",
      overflowWrap: "anywhere",
                  }}
                >
                  <FaEdit size={11} />
                  Ändra automatiskt OB
                </button>
              </div>

              <button
                type="button"
                onClick={handleSaveObCalculationMode}
                disabled={isSavingObMode || (obCalculationMode === savedObCalculationMode && automaticObContinuation === savedAutomaticObContinuation)}
                style={{
                  marginLeft: "auto",
                  padding: "9px 15px",
                  borderRadius: 8,
                  border: "1px solid #16a34a",
                  background: isSavingObMode || (obCalculationMode === savedObCalculationMode && automaticObContinuation === savedAutomaticObContinuation) ? "#dcfce7" : "#16a34a",
                  color: isSavingObMode || (obCalculationMode === savedObCalculationMode && automaticObContinuation === savedAutomaticObContinuation) ? "#166534" : "#ffffff",
                  fontSize: 12,
                  fontWeight: 800,
                  cursor: isSavingObMode || (obCalculationMode === savedObCalculationMode && automaticObContinuation === savedAutomaticObContinuation) ? "not-allowed" : "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  whiteSpace: "normal",
      overflowWrap: "anywhere",
                }}
              >
                <FaSave size={11} />
                {isSavingObMode ? "Sparar..." : "Spara ändringar"}
              </button>

              {obModeSaveMessage && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: obModeSaveMessage === "Sparat!" ? "#15803d" : "#dc2626",
                    padding: "0 6px",
                  }}
                >
                  {obModeSaveMessage}
                </span>
              )}
            </div>
          )}

        </div>

        <div style={styles.body}>
          {activeTab === "time" ? (
            <>
              <div style={styles.tableWrap}>
                <table className="report-table report-table--time" style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Datum</th>
                      <th style={styles.th}>Projekt</th>
                      <th style={styles.th} title="Timmar efter avdrag för rast och frånvaro, utan övertid">Timmar</th>
                      <th style={styles.th}>Rast</th>
                      <th style={styles.th}>Frånvaro</th>
                      <th style={styles.th}>Övertid</th>
                      <th style={styles.th}>Totalt timmar</th>
                      <th style={styles.th}>Tidsunderlag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthRows.length > 0
                      ? monthRows.map((row) => (
                          <tr key={row.id}>
                            <td data-label="Datum" style={styles.td}>{formatDate(row.date)}</td>
                            <td data-label="Projekt" style={styles.td}>{row.project}</td>
                            <td data-label="Timmar" style={styles.td}>{formatHours(row.ordinaryTime)}</td>
                            <td data-label="Rast" style={styles.td}>{formatHours(row.breakTime)}</td>
                            <td data-label="Frånvaro" style={styles.td}>{formatHours(row.absenceTime)}</td>
                            <td data-label="Övertid" style={styles.td}>{formatHours(row.overtimeTime)}</td>
                            <td data-label="Totalt timmar" style={styles.td}>{formatHours(row.workedTime)}</td>
                            <td data-label="Tidsunderlag" style={styles.td}><ReportTimeSource row={row} /></td>
                          </tr>
                        ))
                      : null}

                    <tr>
                      <td data-label="Summa" style={{ ...styles.td, ...styles.summaryCell }} colSpan={2}>
                        Summa
                      </td>
                      <td data-label="Timmar" style={{ ...styles.td, ...styles.summaryCell }}>
                        {formatHours(totals.ordinaryTime)}
                      </td>
                      <td data-label="Rast" style={{ ...styles.td, ...styles.summaryCell }}>
                        {formatHours(totals.breakTime)}
                      </td>
                      <td data-label="Frånvaro" style={{ ...styles.td, ...styles.summaryCell }}>
                        {formatHours(totals.absenceTime)}
                      </td>
                      <td data-label="Övertid" style={{ ...styles.td, ...styles.summaryCell }}>
                        {formatHours(totals.overtimeTime)}
                      </td>
                      <td data-label="Totalt timmar" style={{ ...styles.td, ...styles.summaryCell }}>
                        {formatHours(totals.workedTime)}
                      </td>
                      <td data-label="Tidsunderlag" style={{ ...styles.td, ...styles.summaryCell }} />
                    </tr>
                  </tbody>
                </table>
              </div>

              <div style={styles.footer}>
                <div style={styles.statBox}>
                  <div style={styles.statHeader}>
                    <span>Sjukfrånvaro</span>
                    <span>Total {formatHours(sicknessTotal)}</span>
                  </div>
                  <div style={styles.statRow}>
                    <span>Sjuktimmar</span>
                    <strong>{formatHours(sicknessTotal)}</strong>
                  </div>
                </div>

                <div style={styles.statBox}>
                  <div style={styles.statHeader}>
                    <span>Frånvaro</span>
                    <span>Total {formatHours(totals.absenceTime)}</span>
                  </div>
                  <div style={styles.statRow}>
                    <span>Registrerad frånvaro</span>
                    <strong>{formatHours(totals.absenceTime)}</strong>
                  </div>
                </div>

                <div style={styles.statBox}>
                  <div style={styles.statHeader}>
                    <span>Övertid</span>
                    <span>Total {formatHours(totals.overtimeTime)}</span>
                  </div>
                  <div style={styles.statRow}>
                    <span>Övertidstimmar</span>
                    <strong>{formatHours(totals.overtimeTime)}</strong>
                  </div>
                </div>

              </div>
            </>
          ) : (
            <>
              <div style={styles.tableWrap}>
                <table className="report-table report-table--salary" style={styles.salaryTable}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Datum</th>
                      <th style={styles.th}>Projekt</th>
                      <th style={styles.th}>Pass-tid</th>
                      <th style={styles.th}>OB-detaljer</th>
                      <th style={styles.th}>OB-timmar</th>
                      <th style={styles.th}>OB-värde</th>
                      <th style={styles.th}>Bruttolön</th>
                      <th style={styles.th}>ÖT-timmar</th>
                      <th style={styles.th}>ÖT-tillägg</th>
                      <th style={styles.th}>Semester</th>
                      <th style={styles.th}>Frånvaro</th>
                      <th style={styles.th}>Total</th>
                      <th style={styles.th}>Tidsunderlag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthRows.length > 0
                      ? monthRows.map((row) => (
                          <tr key={row.id}>
                            <td data-label="Datum" style={styles.td}>{formatDate(row.date)}</td>
                            <td data-label="Projekt" style={styles.td}>{row.project}</td>
                            <td data-label="Pass-tid" style={styles.td}>
                              {row.shiftStartTime} - {row.shiftEndTime}
                            </td>
                        <td data-label="OB-detaljer" style={{ ...styles.td, textAlign: "left" }}>
  {row.obDetails && row.obDetails.length > 0 ? (
    <div>
      {row.obDetails.map((ob, idx) => (
        <div
          key={`${ob.sourceType}-${ob.customObId || ob.type}-${idx}`}
          style={{
            fontSize: "11px",
            marginBottom: "4px",
            borderBottom: "1px solid #e5e7eb",
            paddingBottom: "4px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                minWidth: 0,
              }}
            >
              <strong>{ob.type}</strong>
              {(() => {
                const normalizedSourceType = String(ob.sourceType || "").trim().toLowerCase();
                const badge = getObSourceBadge(normalizedSourceType);
                return (
                  <>
                    <span
                      title={`Källa: ${badge.label}`}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "2px 7px",
                        borderRadius: 999,
                        border: `1px solid ${badge.border}`,
                        background: badge.background,
                        color: badge.color,
                        fontSize: 9,
                        fontWeight: 700,
                        lineHeight: 1.2,
                        whiteSpace: "normal",
      overflowWrap: "anywhere",
                        letterSpacing: "0.01em",
                      }}
                    >
                      {badge.label}
                    </span>
                  </>
                );
              })()}
            </div>
            <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
              {approval?.approved === true && <ReportObLockInfo />}
              {ob.sourceType === "custom" && (
                <button
                  type="button"
                  disabled={obEditingDisabled}
                  className="report-ob-edit-button"
                  title="Redigera detta OB"
                  aria-label={`Redigera ${ob.type}`}
                  style={{
                    fontSize: 10,
                    padding: "3px 5px",
                    borderRadius: 4,
                    border: "1px solid #d1d5db",
                    background: "#fff",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                  onClick={() => {
                    const customOb = customObEntries.find(
                      (item) => item.id === ob.customObId
                    );
                    if (customOb) {
                      setSelectedCustomOb(customOb);
                      setIsEditModalOpen(true);
                    }
                  }}
                >
                  <FaEdit size={9} />
                </button>
              )}
              {String(ob.sourceType || "").trim().toLowerCase() !== "automatic" && (
                <button
                  type="button"
                  disabled={obEditingDisabled}
                  className="report-ob-edit-button"
                  title="Lägg till OB för detta pass"
                  aria-label={`Lägg till OB bredvid ${ob.type}`}
                  style={{
                    padding: "3px 6px",
                    borderRadius: 4,
                    border: "1px solid #3b82f6",
                    background: "#eff6ff",
                    color: "#2563eb",
                    cursor: "pointer",
                    fontSize: 10,
                    whiteSpace: "normal",
      overflowWrap: "anywhere",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 3,
                  }}
                  onClick={() => {
                    setSelectedShiftForOb({
                      ...row,
                      initialOb: ob,
                    });
                    setIsAddModalOpen(true);
                  }}
                >
                  <FaPlus size={8} />
                  Redigera OB
                </button>
              )}
            </div>
          </div>
          <div>
            {ob.date && getDateKey(ob.date) !== getDateKey(row.date) && (
              <span style={{ fontWeight: 700, color: "#475569" }}>
                {pad(ob.date.getDate())}/{pad(ob.date.getMonth() + 1)}{" "}
              </span>
            )}
            {ob.startTime}-{ob.endTime}
            <br />
            ({formatHours(ob.durationHours)})
            {ob.rate > 0 && (
              <span style={{ color: "#2563eb" }}>
                {" "}{ob.rate} kr/h
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  ) : (
    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 4 }}>
      {approval?.approved === true && <ReportObLockInfo />}
      <button
        type="button"
                  disabled={obEditingDisabled}
                  className="report-ob-edit-button"
        style={{
          padding: "4px 8px",
          borderRadius: 4,
          border: "1px solid #3b82f6",
          background: "#eff6ff",
          color: "#2563eb",
          cursor: "pointer",
          fontSize: 10,
          whiteSpace: "normal",
      overflowWrap: "anywhere",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}
        onClick={() => {
          setSelectedShiftForOb(row);
          setIsAddModalOpen(true);
        }}
      >
        <FaPlus size={8} />
        Lägg till OB
      </button>
    </div>
  )}
</td>
                            <td data-label="OB-timmar" style={styles.td}>{formatHours(row.obHours)}</td>
                            <td data-label="OB-värde" style={styles.td}>{formatCurrency(row.obValue)}</td>
                            <td data-label="Bruttolön" style={styles.td}>{formatCurrency(row.grossSalary)}</td>
                            <td data-label="ÖT-timmar" style={styles.td}>{formatHours(row.overtimeTime)}</td>
                            <td data-label="ÖT-tillägg" style={styles.td}>{formatCurrency(row.overtimeAddition)}</td>
                            <td data-label="Semester" style={styles.td}>{formatCurrency(row.vacationPay)}</td>
                            <td data-label="Frånvaro" style={styles.td}>{formatNegativeCurrency(row.absenceDeduction)}</td>
                            <td data-label="Total" style={styles.td}>{formatCurrency(row.totalCompensation)}</td>
                            <td data-label="Tidsunderlag" style={styles.td}><ReportTimeSource row={row} /></td>
                          </tr>
                        ))
                      : null}

                    <tr>
                      <td data-label="Summa" style={{ ...styles.td, ...styles.summaryCell }} colSpan={4}>
                        Summa
                      </td>
                      <td data-label="OB-timmar" style={{ ...styles.td, ...styles.summaryCell }}>
                        {formatHours(totals.obHours)}
                      </td>
                      <td data-label="OB-värde" style={{ ...styles.td, ...styles.summaryCell }}>
                        {formatCurrency(totals.obValue)}
                      </td>
                      <td data-label="Bruttolön" style={{ ...styles.td, ...styles.summaryCell }}>
                        {formatCurrency(totals.grossSalary)}
                      </td>
                      <td data-label="ÖT-timmar" style={{ ...styles.td, ...styles.summaryCell }}>
                        {formatHours(totals.overtimeTime)}
                      </td>
                      <td data-label="ÖT-tillägg" style={{ ...styles.td, ...styles.summaryCell }}>
                        {formatCurrency(totals.overtimeAddition)}
                      </td>
                      <td data-label="Semester" style={{ ...styles.td, ...styles.summaryCell }}>
                        {formatCurrency(totals.vacationPay)}
                      </td>
                      <td data-label="Frånvaro" style={{ ...styles.td, ...styles.summaryCell }}>
                        {formatNegativeCurrency(totals.absenceDeduction)}
                      </td>
                      <td data-label="Total" style={{ ...styles.td, ...styles.summaryCell }}>
                        {formatCurrency(totals.totalCompensation)}
                      </td>
                      <td data-label="Tidsunderlag" style={{ ...styles.td, ...styles.summaryCell }} />
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="report-salary-summary" aria-label="Lönesammanfattning">
                <div className="report-salary-card">
                  <span className="report-salary-label">Bruttolön</span>
                  <strong className="report-salary-amount">{formatCurrency(grossSalaryTotal)}</strong>
                  <span className="report-salary-note">Total ersättning före skatt</span>
                </div>
                <div className="report-salary-card report-salary-card--net">
                  <span className="report-salary-label">Nettolön</span>
                  <strong className="report-salary-amount">{formatCurrency(netSalaryTotal)}</strong>
                  <div className="report-salary-tax">
                    <span>Skatt ({taxRate.toLocaleString("sv-SE")}%)</span>
                    <span>{formatNegativeCurrency(grossSalaryTotal - netSalaryTotal)}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <AutomaticObAmountModal
        isOpen={isAutomaticObAmountModalOpen}
        onClose={() => setIsAutomaticObAmountModalOpen(false)}
        onSave={handleSaveAutomaticObAmount}
        currentManualAmount={automaticObManualAmount}
      />

      <AddCustomObModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setSelectedShiftForOb(null);
        }}
        onSave={handleAddCustomOb}
        shiftId={selectedShiftForOb?.id}
        shiftDate={selectedShiftForOb?.date}
        shiftStartTime={selectedShiftForOb?.shiftStartTime}
        shiftEndTime={selectedShiftForOb?.shiftEndTime}
        initialOb={selectedShiftForOb?.initialOb || null}
        existingCustomObs={customObEntries}
      />

      <EditCustomObModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedCustomOb(null);
        }}
        onSave={handleUpdateCustomOb}
        onDelete={handleDeleteCustomOb}
        customOb={selectedCustomOb}
      />
    </section>
  );
}

// Styles live with this component; keep selectors and responsive rules together.
registerStyles("TimeReportPopup", `
.time-report-page *,
.time-report-page *::before,
.time-report-page *::after {
  box-sizing: border-box;
}

.time-report-page .report-table td > div,
.time-report-page .report-table td div[style] {
  min-width: 0;
  flex-wrap: wrap;
}

.time-report-page .report-table--salary th:nth-child(4) {
  width: 22%;
}

@container (max-width: 1150px) {
  .report-table--salary,
  .report-table--salary tbody {
    display: block;
  }

  .report-table--salary thead {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }

  .report-table--salary tr {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    border-bottom: 3px solid #cbd5e1;
  }

  .report-table--salary td {
    display: block;
    min-width: 0;
  }

  .report-table--salary td[data-label="OB-detaljer"],
  .report-table--salary td[colspan] {
    grid-column: 1 / -1;
  }

  .report-table--salary td::before {
    content: attr(data-label);
    display: block;
    margin-bottom: 6px;
    color: #64748b;
    font-size: 11px;
    font-weight: 700;
  }
}

@container (max-width: 700px) {
  .report-table--time,
  .report-table--time tbody {
    display: block;
  }

  .report-table--time thead {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }

  .report-table--time tr,
  .report-table--salary tr {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    border-bottom: 3px solid #cbd5e1;
  }

  .report-table--time td {
    display: block;
    min-width: 0;
  }

  .report-table--time td[colspan] {
    grid-column: 1 / -1;
  }

  .report-table--time td::before {
    content: attr(data-label);
    display: block;
    margin-bottom: 6px;
    color: #64748b;
    font-size: 11px;
    font-weight: 700;
  }
}


.time-report-page .report-salary-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.time-report-page .report-salary-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.03);
}

.time-report-page .report-salary-label {
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
}

.time-report-page .report-salary-amount {
  color: #0f172a;
  font-size: clamp(20px, 2cqi, 24px);
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
  line-height: 1.2;
}

.time-report-page .report-salary-note,
.time-report-page .report-salary-tax {
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid #e2e8f0;
  font-size: 12px;
  color: #64748b;
}

.time-report-page .report-salary-card--net {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.time-report-page .report-salary-card--net .report-salary-label,
.time-report-page .report-salary-card--net .report-salary-amount {
  color: #1e40af;
}

.time-report-page .report-salary-tax {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  border-color: #bfdbfe;
  color: #475569;
  font-variant-numeric: tabular-nums;
}

@container (max-width: 550px) {
  .time-report-page .report-salary-summary {
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
  }
}


.time-report-page .report-approval {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
}

.time-report-page .report-approval-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 18px;
}

.time-report-page .report-approval-label {
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.time-report-page .report-approval-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 9px;
  border-radius: 999px;
  background: #dcfce7;
  color: #166534;
  font-size: 12px;
  font-weight: 700;
}

.time-report-page .report-approval-dot {
  width: 6px;
  height: 6px;
  flex-shrink: 0;
  border-radius: 50%;
  background: currentColor;
}

.time-report-page .report-approval-details {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.time-report-page .report-approval-details strong {
  color: #334155;
  font-weight: 600;
}

.time-report-page .report-approval-date {
  font-variant-numeric: tabular-nums;
}

.time-report-page .report-approval-badge--pending {
  background: #edf0f5;
  color: #596579;
}

.time-report-page .report-ob-edit-button:disabled {
  background: #e8ebf0 !important;
  border-color: #cbd5e1 !important;
  color: #475569 !important;
  opacity: 1;
  cursor: not-allowed !important;
}

.report-payroll-lock {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.report-payroll-lock-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #475569;
  cursor: pointer;
}

.report-payroll-lock-button:hover,
.report-payroll-lock-button:focus-visible {
  background: #e2e8f0;
  outline: 2px solid #94a3b8;
}

.report-payroll-lock-bubble {
  position: fixed;
  z-index: 2000;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #ffffff;
  color: #334155;
  box-shadow: 0 10px 25px rgba(15, 23, 42, .18);
  font-size: 12px;
  font-weight: 500;
  line-height: 1.45;
  text-align: left;
  white-space: normal;
  pointer-events: auto;
}

.time-report-page .report-time-source { display: flex; flex-wrap: wrap; justify-content: flex-end; align-items: center; gap: 6px; min-width: 145px; }
.time-report-page .report-time-source-badge { padding: 4px 8px; border-radius: 999px; background: #edf0f5; color: #596579; font-size: 11px; font-weight: 600; line-height: 1.4; }
.time-report-page .report-time-source-badge--punched { background: #ede9fe; color: #6d28d9; }
.time-report-page .report-time-source-details summary { display: flex; justify-content: flex-end; cursor: pointer; color: #6d28d9; padding: 4px; list-style: none; }
.time-report-page .report-time-source-details summary::-webkit-details-marker { display: none; }
.time-report-page .report-time-source-details summary:focus-visible { outline: 2px solid #6d28d9; border-radius: 4px; }
.time-report-page .report-time-source-details[open] { width: 100%; }
.time-report-page .report-time-source-history { display: grid; gap: 6px; margin-top: 6px; padding: 10px; border: 1px solid #ddd6fe; border-radius: 8px; background: #faf8ff; color: #475569; font-size: 12px; line-height: 1.5; text-align: left; }
`);
